import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ArrowLeft, ArrowRight, X, MessageCircle, CalendarDays, Check, FileText,
  BedDouble, Ruler, MapPin, ExternalLink,
  Building2, Umbrella, Sparkles, TrendingUp, Waves, Info, Send, CheckCircle2,
  Gauge, Handshake, Layers, Search, Wallet, Activity, Play, Plus,
} from 'lucide-react';
import { SERVICES, ServiceItem } from '../data/servicesCatalog';
import { useLanguage } from '../components/LanguageContext';
import { openWhatsApp, openCalendly, WHATSAPP_PM_NUMBER } from './shared/contact';
import { TERMS_URL, PRIVACY_URL, openPolicy } from './shared/legal';
import {
  fetchSellListings, formatAed, formatAedFull, submitEnquiry,
  joinFractionalWaitlist, SellListing,
} from './shared/sellListings';
import './shared/portals.css';

// ============================================================
// Pre-login showcase, opened from the welcome screen.
//
// Three tabs, because the three things on offer are unrelated errands:
// browsing units to buy, booking a seat on the Dubai trip, and reading
// what we handle around a purchase. Stacking all three made one endless
// scroll; tabs let a visitor land on what they came for.
//
// Units are real Dubai Sell inventory read live from
// /api/sell-platform/listings (see shared/sellListings) — the same
// properties dreamhaven.ae shows — so the gallery is never stocked with
// placeholder photography.
//
// The journey ticket is bought inside an in-app frame rather than by
// bouncing the visitor out to Ablefy.
//
// Visual language follows the in-app service cards in MainDashboard —
// obsidian ground, gold-gradient tiles, dimensional cards — so pre-login
// and signed-in read as one product.
// ============================================================

// The trip's booking page. Framed in-app; the external fallback exists
// because a later checkout step may refuse to be framed.
const JOURNEY_URL = 'https://myablefy.com/s/investmenter/reise-dubai';

// How many unit cards to show before "Show more".
const UNITS_PAGE = 9;

// Skyline plate behind the hero. Served from public/ like the wordmark, and
// applied as an inline background so a missing file degrades to the gradient
// rather than breaking the build or showing a torn image box.
const HERO_IMAGE = '/showcase-hero.jpg';

// Banner for the Stays tab. An AI-generated impression of the Dubai Islands
// beachfront, credited as such on the page — the six apartments are still
// being furnished, so no photograph of them exists to use yet.
const STAYS_IMAGE = '/showcase-stays.jpg';
const JOURNEY_IMAGE = '/showcase-journey.jpg';
// The MasterMind teaser, transcoded from the 88 MB master down to 9.6 MB
// at 720p with faststart, so it can begin playing without buffering the
// whole file.
const JOURNEY_FILM = '/journey/mastermind.mp4';
const JOURNEY_FILM_POSTER = '/journey/mastermind-poster.jpg';

// Home tab imagery (Higgsfield, corporate ink-and-gold palette).
const HOME_HERO = '/home/hero.jpg';
const HOME_HERO_VIDEO = '/home/hero-loop.mp4';
const HOME_FILM = '/home/how-it-works.mp4';
const HOME_FAM_HERO = '/home/family-hero.jpg';

// Portraits are hosted here rather than hot-linked from dubaisellfast.com, so
// this section does not break if that site changes.
const HOME_TEAM = [
  { img: '/team/michael.jpg', name: 'Michael', roleKey: 'scHomeRoleChairman' },
  { img: '/team/jim.jpg', name: 'Jim', roleKey: 'scHomeRoleFounder' },
  { img: '/team/brigitte.jpg', name: 'Brigitte', roleKey: 'scHomeRoleFounder' },
  { img: '/team/kiano.jpg', name: 'Kiano', roleKey: 'scHomeRoleFounder' },
];

// Investor testimonials, moved here from the signed-in owner area. Wistia ids
// and copy are unchanged; the ip* keys come from i18n/investor.ts, which merges
// into the same t(), so there is one copy of these strings in the codebase.
// autoPlay needs muted - browsers block audible autoplay - and lazy loading
// keeps three video players from all starting at once on page load.
const HOME_VOICES = [
  { id: 'jens-rabe', name: 'Jens Rabe', wistia: 'xhjj13yti9', titleKey: 'ipTestimonial1Title', quoteKey: 'ipTestimonial1Quote' },
  { id: 'jorg-lohr', name: 'J\u00f6rg L\u00f6hr', wistia: '3zo7fea3eo', titleKey: 'ipTestimonial2Title', quoteKey: 'ipTestimonial2Quote' },
  { id: 'stephan-katja', name: 'Stephan & Katja', wistia: 'dou708rfi1', titleKey: 'ipTestimonial3Title', quoteKey: 'ipTestimonial3Quote' },
];
const HOME_FAM = [
  { img: '/home/family-school.jpg', titleKey: 'scHomeFam1Title', bodyKey: 'scHomeFam1', altKey: 'scHomeFam1Alt' },
  { img: '/home/family-park.jpg', titleKey: 'scHomeFam2Title', bodyKey: 'scHomeFam2', altKey: 'scHomeFam2Alt' },
  { img: '/home/family-marina.jpg', titleKey: 'scHomeFam3Title', bodyKey: 'scHomeFam3', altKey: 'scHomeFam3Alt' },
];

// The four checks a developer has to survive before its units appear here.
// The numerals are ordinals rather than counts, and read the same in both
// languages, so they stay literal; every word comes from i18n.
const HOME_GATE = [
  { n: '01', titleKey: 'scGate1Title', bodyKey: 'scGate1' },
  { n: '02', titleKey: 'scGate2Title', bodyKey: 'scGate2' },
  { n: '03', titleKey: 'scGate3Title', bodyKey: 'scGate3' },
  { n: '04', titleKey: 'scGate4Title', bodyKey: 'scGate4' },
];

// Property management, marketed out of the product's own vocabulary. These
// five stages and their one-line descriptions are the very pmStep* keys the
// owner's Property Management screen uses, so the pitch here and the software
// an owner is handed can never end up describing the job differently.
// The same number as WHATSAPP_PM_NUMBER, spaced for reading. Kept beside it so
// the two are edited together; the wa.me link never uses this form.
const PM_WHATSAPP_DISPLAY = '+971 52 886 2187';

// Three screens from the owner's app, drawn rather than screenshotted - a real
// capture would carry a real owner's name, unit and rent. Dashboard sits in the
// middle and is raised, because it is the one an owner opens daily.
const PM_SCREENS = [
  { src: '/pm/app-inspection.svg', altKey: 'scPmShotInsAlt', capKey: 'scPmShotInsCap' },
  { src: '/pm/app-screen.svg', altKey: 'scPmShotAlt', capKey: 'scPmShotDashCap' },
  { src: '/pm/app-expenses.svg', altKey: 'scPmShotExpAlt', capKey: 'scPmShotExpCap' },
];

// Property management imagery (Higgsfield, corporate ink-and-gold palette),
// same provenance as the Home tab's plates. The banner opens the tab on the
// thing being looked after; the keys close it.
const PM_HERO = '/pm/hero.jpg';
const PM_HERO_VIDEO = '/pm/hero-loop.mp4';
const PM_KEYS = '/pm/keys.jpg';
// The five monitor screens live on PM_STAGES, one per stage - see below.
// Drawn rather than photographed for the same reason as the phones: a real
// capture carries a real owner's figures, and a stock photo of a monitor shows
// a chart that means nothing.

// Each stage carries the screen that stage produces, so the monitor beside the
// ladder always shows the step being read rather than one fixed dashboard that
// only ever matched stage V.
const PM_STAGES = [
  { n: 'I', titleKey: 'pmStepSnagging', bodyKey: 'pmStepSnaggingDesc',
    shot: '/pm/desk-snagging.svg', altKey: 'scPmDeskSnagAlt', capKey: 'scPmDeskSnagCap' },
  { n: 'II', titleKey: 'pmStepHandover', bodyKey: 'pmStepHandoverDesc',
    shot: '/pm/desk-handover.svg', altKey: 'scPmDeskHandAlt', capKey: 'scPmDeskHandCap' },
  { n: 'III', titleKey: 'pmStepPlacement', bodyKey: 'pmStepPlacementDesc',
    shot: '/pm/desk-tenant.svg', altKey: 'scPmDeskTenAlt', capKey: 'scPmDeskTenCap' },
  { n: 'IV', titleKey: 'pmStepContracts', bodyKey: 'pmStepContractsDesc',
    shot: '/pm/desk-contracts.svg', altKey: 'scPmDeskConAlt', capKey: 'scPmDeskConCap' },
  { n: 'V', titleKey: 'pmStepRental', bodyKey: 'pmStepRentalDesc',
    shot: '/pm/app-desktop.svg', altKey: 'scPmDeskAlt', capKey: 'scPmDeskCap' },
];

// What the managed service actually does, in three groups: get it earning,
// keep it right, leave the owner alone. Every line is drawn from a capability
// the owner's PM screen already carries - inspection reports, in-house
// maintenance, the asset inventory, retention, cost approval - rather than
// from a list of things a managing agent could theoretically offer.
const PM_BENEFITS: { titleKey: string; items: { titleKey: string; bodyKey: string }[] }[] = [
  {
    titleKey: 'scPmGrp1Title',
    items: [
      { titleKey: 'scPmB1Title', bodyKey: 'scPmB1' },
      { titleKey: 'scPmB2Title', bodyKey: 'scPmB2' },
      { titleKey: 'scPmB3Title', bodyKey: 'scPmB3' },
      { titleKey: 'scPmB4Title', bodyKey: 'scPmB4' },
    ],
  },
  {
    titleKey: 'scPmGrp2Title',
    items: [
      { titleKey: 'scPmB5Title', bodyKey: 'scPmB5' },
      { titleKey: 'scPmB6Title', bodyKey: 'scPmB6' },
      { titleKey: 'scPmB7Title', bodyKey: 'scPmB7' },
      { titleKey: 'scPmB8Title', bodyKey: 'scPmB8' },
    ],
  },
  {
    titleKey: 'scPmGrp3Title',
    items: [
      { titleKey: 'scPmB9Title', bodyKey: 'scPmB9' },
      { titleKey: 'scPmB10Title', bodyKey: 'scPmB10' },
      { titleKey: 'scPmB11Title', bodyKey: 'scPmB11' },
      { titleKey: 'scPmB12Title', bodyKey: 'scPmB12' },
    ],
  },
];

// What an owner sees once a property is under management. Every row is a panel
// that exists on that screen today - deliberately not a wish list.
const PM_SCREEN = [
  { icon: Wallet, titleKey: 'scPmScreen1Title', bodyKey: 'scPmScreen1' },
  { icon: Gauge, titleKey: 'scPmScreen2Title', bodyKey: 'scPmScreen2' },
  { icon: CalendarDays, titleKey: 'scPmScreen3Title', bodyKey: 'scPmScreen3' },
  { icon: FileText, titleKey: 'scPmScreen4Title', bodyKey: 'scPmScreen4' },
  { icon: Activity, titleKey: 'scPmScreen5Title', bodyKey: 'scPmScreen5' },
  { icon: Layers, titleKey: 'scPmScreen6Title', bodyKey: 'scPmScreen6' },
];

// Which tab each "way in" card opens, with the line that sells it.
const HOME_WAYS: { key: Tab; descKey: string }[] = [
  { key: 'units', descKey: 'scHomeInsideUnits' },
  { key: 'fractional', descKey: 'scHomeInsideFractional' },
  { key: 'stays', descKey: 'scHomeInsideStays' },
  { key: 'pm', descKey: 'scHomeInsidePm' },
  { key: 'journey', descKey: 'scHomeInsideJourney' },
  { key: 'services', descKey: 'scHomeInsideServices' },
];

// Antavio media. Stills are re-encoded into public/antavio; the videos are
// streamed from Antavio's own hosting because crest-video.mp4 is 19 MB and
// this bundle installs onto phones. The site CSP already allows
// `media-src 'self' https:` and https images.
const ANT_CREST_IMG = '/antavio/crest-exterior.jpg';
const ANT_BALCONY_IMG = '/antavio/crest-balcony.jpg';
const ANT_APP_SVG = '/antavio/app-dashboard.svg';
const ANT_TEAM_IMG =
  'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=900&q=80';
const ANT_HOST = 'https://antavio-c7405.web.app/assets/assets/properties';
const ANT_CREST_VIDEO = `${ANT_HOST}/crest-video.60103fa7152d1f1312472993bea45d82.mp4`;
const ANT_EXPLAINER_EN = `${ANT_HOST}/explainer-en.62e89bf9717cf9c76b20d34cdfc39ebe.mp4`;
const ANT_EXPLAINER_DE = `${ANT_HOST}/explainer-de.0c004b233609571c10331c41519e3a4c.mp4`;

// Live short-stay apartments, bookable on our own Beds24 reservation system.
// Both booking pages allow framing, so a guest books inside the app rather
// than being handed off.
const STAYS: { id: string; propId: string; nameKey: string; subKey: string }[] = [
  { id: 'aurora', propId: '334711', nameKey: 'scStayA', subKey: 'scStayASub' },
  { id: 'aurelia', propId: '336238', nameKey: 'scStayB', subKey: 'scStayBSub' },
];

const bookingUrl = (propId: string) => `https://beds24.com/booking2.php?propid=${propId}`;

// Developer partner logos, sliced out of the client's composite PNG by
// scratchpad/slice_band.js. The composite is the only source with the correct
// artwork for samana, imtiaz and avenew — the standalone files for those three
// were wrong — but a single flat image cannot reflow, so on a phone each mark
// rendered ~50px wide and unreadable. Individual files let the grid drop to two
// columns at a legible size. Re-run the slicer if the composite is replaced.
const DEVELOPERS: { slug: string; name: string }[] = [
  { slug: 'emaar', name: 'Emaar' },
  { slug: 'meraas', name: 'Meraas' },
  { slug: 'nakheel', name: 'Nakheel' },
  { slug: 'omniyat', name: 'Omniyat' },
  { slug: 'select-group', name: 'Select Group' },
  { slug: 'sobha', name: 'Sobha' },
  { slug: 'beyond', name: 'Beyond Developments' },
  { slug: 'imtiaz', name: 'Imtiaz Developments' },
  { slug: 'samana', name: 'Samana Developers' },
  { slug: 'avenew', name: 'Avenew Development' },
];

type Tab = 'home' | 'units' | 'fractional' | 'stays' | 'pm' | 'journey' | 'services';

interface ServicesShowcaseProps {
  onClose: () => void;
  /**
   * Which tab to land on. Exists because the pre-login App Tour sends a
   * prospect straight to Management: the tour makes the case for the service
   * and this sheet carries the detail, so dropping them on Home would make
   * them hunt for the page they just asked for.
   */
  initialTab?: Tab;
}

// The six stages, in the order an investor lives them. Anything in the
// catalogue not listed falls through to the last stage, so adding a
// service can never make it silently disappear.
const CHAPTERS: { numeral: string; titleKey: string; ledeKey: string; ids: string[] }[] = [
  { numeral: 'I', titleKey: 'scCh1Title', ledeKey: 'scCh1Lede',
    ids: ['company-formation', 'holdings-foundations', 'international-tax', 'tax-expert', 'bank-account', 'mortgage'] },
  { numeral: 'II', titleKey: 'scCh2Title', ledeKey: 'scCh2Lede',
    ids: ['escrow', 'poa', 'document-attestation', 'dld-update'] },
  { numeral: 'III', titleKey: 'scCh3Title', ledeKey: 'scCh3Lede',
    ids: ['snagging', 'property-handover', 'interior-design', 'fit-out', 'key-duplication'] },
  { numeral: 'IV', titleKey: 'scCh4Title', ledeKey: 'scCh4Lede',
    ids: ['property-management', 'airbnb', 'unit-insurance', 'ongoing-tax-support'] },
  { numeral: 'V', titleKey: 'scCh5Title', ledeKey: 'scCh5Lede',
    ids: ['golden-visa', 'emigration-visa', 'emigration-exit-tax', 'asset-protection', 'last-will'] },
  { numeral: 'VI', titleKey: 'scCh6Title', ledeKey: 'scCh6Lede',
    ids: ['resell-transfer', 'gifting-individual', 'gifting-company'] },
];

// Waiting-list questions, adapted from the Brikk prototype's opt-in wizard.
// One question per step keeps the flow light for an older audience.
const FR_QUESTIONS: { id: string; titleKey: string; optionKeys: string[] }[] = [
  { id: 'market', titleKey: 'scFrQCountry', optionKeys: ['scFrQCountryO1', 'scFrQCountryO2', 'scFrQCountryO3', 'scFrQCountryO4'] },
  { id: 'interest', titleKey: 'scFrQInterest', optionKeys: ['scFrQInterestO1', 'scFrQInterestO2', 'scFrQInterestO3', 'scFrQInterestO4'] },
  { id: 'amount', titleKey: 'scFrQAmount', optionKeys: ['scFrQAmountO1', 'scFrQAmountO2', 'scFrQAmountO3', 'scFrQAmountO4'] },
  { id: 'timing', titleKey: 'scFrQTiming', optionKeys: ['scFrQTimingO1', 'scFrQTimingO2', 'scFrQTimingO3', 'scFrQTimingO4'] },
];

// Bedroom filters. `test` runs against a listing; `all` matches everything.
const FILTERS: { key: string; labelKey: string; test: (l: SellListing) => boolean }[] = [
  { key: 'all', labelKey: 'scFilterAll', test: () => true },
  { key: 'deals', labelKey: 'scFilterDeals', test: (l) => l.isDistress },
  { key: 'studio', labelKey: 'scFilterStudio', test: (l) => l.bedrooms === 0 },
  { key: 'b1', labelKey: 'scFilter1', test: (l) => l.bedrooms === 1 },
  { key: 'b2', labelKey: 'scFilter2', test: (l) => l.bedrooms === 2 },
  { key: 'b3', labelKey: 'scFilter3', test: (l) => (l.bedrooms ?? 0) >= 3 },
];

// Primary navigation. Order is the order of intent: browse, stay, visit, read.
const TABS: { key: Tab; labelKey: string; capKey: string }[] = [
  { key: 'home', labelKey: 'scTabHome', capKey: 'scTabHomeCap' },
  { key: 'units', labelKey: 'scTabUnits', capKey: 'scTabUnitsCap' },
  { key: 'fractional', labelKey: 'scTabFractional', capKey: 'scTabFractionalCap' },
  { key: 'stays', labelKey: 'scTabStays', capKey: 'scTabStaysCap' },
  // After Stays, before Journey: the order follows what an owner does, and
  // management is what happens once the keys are actually theirs.
  { key: 'pm', labelKey: 'scTabPm', capKey: 'scTabPmCap' },
  { key: 'journey', labelKey: 'scTabJourney', capKey: 'scTabJourneyCap' },
  { key: 'services', labelKey: 'scTabServices', capKey: 'scTabServicesCap' },
];

// The Property Owner catalogue verbatim — the approved list, nothing added.
const ALL_SERVICES: ServiceItem[] = SERVICES;

function buildChapters() {
  const byId = new Map(ALL_SERVICES.map((s) => [s.id, s]));
  const placed = new Set<string>();

  const chapters = CHAPTERS.map((ch) => {
    const items: ServiceItem[] = [];
    ch.ids.forEach((id) => {
      const svc = byId.get(id);
      if (svc) { items.push(svc); placed.add(id); }
    });
    return { ...ch, items };
  });

  const leftover = ALL_SERVICES.filter((s) => !placed.has(s.id));
  if (leftover.length) chapters[chapters.length - 1].items.push(...leftover);

  return chapters.filter((ch) => ch.items.length > 0);
}

const TileIcon: React.FC<{ icon: React.ReactNode }> = ({ icon }) => (
  <>{React.isValidElement(icon)
    ? React.cloneElement(icon as React.ReactElement<any>, { size: 21, strokeWidth: 1.9 })
    : icon}</>
);

/* A figure that counts up once to its value, per DESIGN_BRIEF.md section 6
   (~1000ms, ease-out). The strings carry prefixes and suffixes - "~EUR 350M",
   "35 years", "100%" - so only the numeric run animates and everything around
   it is preserved verbatim. Anything that does not parse is rendered as-is,
   which keeps the brief's "every figure must be real" rule safe: this never
   invents or reformats a number it did not understand.
   Reduced motion shows the final value immediately. */
function CountUpFigure({ value }: { value: string }) {
  const ref = useRef<HTMLElement | null>(null);
  const [shown, setShown] = useState(value);

  useEffect(() => {
    setShown(value);
    const el = ref.current;
    if (!el) return;

    const m = value.match(/\d[\d.,]*/);
    if (!m) return;
    const body = m[0];
    const prefix = value.slice(0, m.index);
    const suffix = value.slice((m.index || 0) + body.length);

    // Decide whether the separators are thousands groups or a decimal mark.
    const parts = body.split(/[.,]/);
    const grouped = parts.length > 1 && parts.slice(1).every((g) => g.length === 3);
    const sep = grouped ? body[body.search(/[.,]/)] : '';
    const decimals = grouped || parts.length === 1 ? 0 : parts[parts.length - 1].length;
    const target = grouped
      ? Number(parts.join(''))
      : Number(body.replace(',', '.'));
    if (!isFinite(target)) return;

    const fmt = (n: number) => {
      const s = decimals
        ? n.toFixed(decimals)
        : String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, sep || '');
      return prefix + s + suffix;
    };

    const reduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || typeof IntersectionObserver === 'undefined') return;

    let raf = 0;
    let start = 0;
    let done = false;
    const DUR = 1000;

    const step = (ts: number) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / DUR);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic, no overshoot
      setShown(fmt(target * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (done || !entries.some((e) => e.isIntersecting)) return;
        done = true;
        io.disconnect();
        setShown(fmt(0));
        raf = requestAnimationFrame(step);
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value]);

  return <b ref={ref as React.RefObject<HTMLElement>}>{shown}</b>;
}

const ServicesShowcase: React.FC<ServicesShowcaseProps> = ({ onClose, initialTab = 'home' }) => {
  const { t, language, setLanguage } = useLanguage();
  // Counted, not typed in, so the Home figures cannot drift from the
  // catalogue. Anything still flagged comingSoon is excluded.
  const serviceById = useMemo(
    () => new Map(ALL_SERVICES.map((x) => [x.id, x])),
    [],
  );
  const liveServiceCount = useMemo(
    () => ALL_SERVICES.filter((x) => !x.comingSoon).length,
    [],
  );
  const de = language === 'de';
  const chapters = useMemo(buildChapters, []);

  const [tab, setTab] = useState<Tab>(initialTab);
  // Which of the five management stages the monitor beside the ladder shows.
  const [pmStage, setPmStage] = useState(0);
  const [filterKey, setFilterKey] = useState('all');
  const [visibleCount, setVisibleCount] = useState(UNITS_PAGE);
  const [stageIdx, setStageIdx] = useState(0);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [unit, setUnit] = useState<SellListing | null>(null);
  // One in-app browser for both the journey ticket and stay bookings.
  const [frame, setFrame] = useState<{ url: string; label: string } | null>(null);

  // Lead capture on the unit sheet. This is the page's conversion point:
  // it posts a real lead into the Dubai Sell `leads` table plus Slack.
  // Waiting list for fractional ownership. No licence yet, so this collects
  // interest only — it is not a subscription and makes no offer.
  const [frStep, setFrStep] = useState(0);
  const [frAnswers, setFrAnswers] = useState<Record<string, string>>({});
  const [frForm, setFrForm] = useState({ name: '', email: '', phone: '', note: '' });
  const [frConsent, setFrConsent] = useState(false);
  const [frState, setFrState] = useState<'idle' | 'sending' | 'done'>('idle');
  const [frHint, setFrHint] = useState<string | null>(null);
  const frFormRef = useRef<HTMLDivElement | null>(null);
  /* Scroll reveals and figure count-ups, per DESIGN_BRIEF.md section 6.
     Targets are collected here rather than hand-tagged in the markup so every
     band picks them up automatically. Elements already on screen are marked
     revealed immediately - hiding them after first paint would flash. */
  useEffect(() => {
    if (tab !== 'fractional') return;
    const root = frRootRef.current;
    if (!root) return;
    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const nodes = Array.from(
      root.querySelectorAll<HTMLElement>(
        '.sc-fr-body > .sc-wrap > *, .sc-fr-white > .sc-wrap > *,' +
          '.sc-fr-navy-deep > .sc-wrap > *, .sc-fr-trust-band > .sc-wrap > *'
      )
    );
    if (!nodes.length) return;

    if (reduce || typeof IntersectionObserver === 'undefined') {
      nodes.forEach((n) => n.classList.add('ant-reveal', 'is-in'));
      return;
    }

    const fold = window.innerHeight * 0.9;
    const pending: HTMLElement[] = [];
    nodes.forEach((n) => {
      n.classList.add('ant-reveal');
      if (n.getBoundingClientRect().top < fold) n.classList.add('is-in');
      else pending.push(n);
    });
    if (!pending.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-in');
          io.unobserve(e.target); // reveal once, never on the way back up
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
    );
    pending.forEach((n) => io.observe(n));

    /* Safety net. The observer only fires for elements that actually cross
       the viewport, so an instant jump - clicking "Join the early-access
       list" scrolls straight to the form - can skip over whole bands and
       leave them stuck at opacity 0. Sweeping on scroll reveals anything the
       reader has reached or passed, which is the correct outcome either way.
       rAF-throttled and passive so it costs nothing while scrolling. */
    let queued = false;
    const sweep = () => {
      queued = false;
      const limit = window.innerHeight * 0.9;
      for (let i = pending.length - 1; i >= 0; i -= 1) {
        const n = pending[i];
        if (n.getBoundingClientRect().top < limit) {
          n.classList.add('is-in');
          io.unobserve(n);
          pending.splice(i, 1);
        }
      }
      if (!pending.length) window.removeEventListener('scroll', onScroll);
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(sweep);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      io.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, [tab]);

  const [faqOpen, setFaqOpen] = useState<number | null>(1);
  const frRootRef = useRef<HTMLElement | null>(null);
  const explainerRef = useRef<HTMLVideoElement | null>(null);
  const [explainerStarted, setExplainerStarted] = useState(false);
  const filmRef = useRef<HTMLVideoElement | null>(null);
  const [heroFilmOk, setHeroFilmOk] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const wide = window.matchMedia('(min-width: 900px)').matches;
    const calm = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const conn = (navigator as unknown as { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    const thrifty = !!conn
      && (conn.saveData === true || /(^|-)(2g|3g)$/.test(String(conn.effectiveType || '')));
    setHeroFilmOk(wide && calm && !thrifty);
  }, []);
  const [filmStarted, setFilmStarted] = useState(false);
  const journeyFilmRef = useRef<HTMLVideoElement | null>(null);
  const [journeyPlaying, setJourneyPlaying] = useState(false);
  const [stop, setStop] = useState(0);
  const [roadAuto, setRoadAuto] = useState(true);
  const voicesRef = useRef<HTMLDivElement | null>(null);
  const [voicesLive, setVoicesLive] = useState(false);

  /* The testimonial players are created only once the section is on screen.
     loading="lazy" was not enough: the iframe would initialise off screen,
     Chrome refuses autoplay there, and Wistia fell back to a play button.
     Mounting on intersection means the player's autoplay attempt happens
     while it is visible, which is when it is permitted. */
  useEffect(() => {
    if (tab !== 'home' || voicesLive) return;
    const el = voicesRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') { setVoicesLive(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setVoicesLive(true);
    }, { rootMargin: '200px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [tab, voicesLive]);
  const roadRef = useRef<HTMLDivElement | null>(null);

  /* The road walks itself I -> VI so the sequence is visible without anyone
     touching it. It hands control over permanently the moment the reader
     picks a stop, holds while the section is off screen, and never runs at
     all under prefers-reduced-motion. */
  useEffect(() => {
    if (tab !== 'home' || !roadAuto) return;
    if (
      typeof window === 'undefined' ||
      (typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    ) {
      return;
    }

    let onScreen = true;
    let io: IntersectionObserver | undefined;
    const el = roadRef.current;
    if (el && typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(
        (entries) => { onScreen = entries.some((e) => e.isIntersecting); },
        { threshold: 0.3 },
      );
      io.observe(el);
    }

    const timer = window.setInterval(() => {
      if (onScreen) setStop((s) => (s + 1) % CHAPTERS.length);
    }, 3800);

    return () => {
      window.clearInterval(timer);
      io?.disconnect();
    };
  }, [tab, roadAuto]);

  // Any deliberate choice ends the walk - it must never fight the reader.
  const pickStop = useCallback((i: number) => {
    setStop(i);
    setRoadAuto(false);
  }, []);
  const [filmShot, setFilmShot] = useState(0);
  const onFilmTime = useCallback(() => {
    const v = filmRef.current;
    if (!v || !v.duration || !isFinite(v.duration)) return;
    const i = Math.floor((v.currentTime / v.duration) * CHAPTERS.length);
    setFilmShot(Math.max(0, Math.min(CHAPTERS.length - 1, i)));
  }, []);

  const scrollToForm = () =>
    frFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const [lead, setLead] = useState({ name: '', email: '', phone: '', message: '' });
  const [leadState, setLeadState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [leadHint, setLeadHint] = useState<string | null>(null);

  const [listings, setListings] = useState<SellListing[]>([]);
  const [total, setTotal] = useState(0);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');

  const active = activeId ? ALL_SERVICES.find((s) => s.id === activeId) ?? null : null;
  const overlayOpen = !!active || !!unit || !!frame;

  const openUnit = (l: SellListing) => {
    setUnit(l);
    setLead({ name: '', email: '', phone: '', message: '' });
    setLeadState('idle');
    setLeadHint(null);
  };

  const sendLead = async () => {
    if (!unit) return;
    const name = lead.name.trim();
    const email = lead.email.trim();
    // Deliberately loose: enough to catch a typo, not enough to reject a
    // valid address the regex has never heard of.
    if (name.length < 2 || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      setLeadHint(t('scLeadRequired'));
      return;
    }
    setLeadHint(null);
    setLeadState('sending');
    try {
      await submitEnquiry({ listing: unit, name, email, phone: lead.phone.trim(), message: lead.message.trim() });
      setLeadState('done');
    } catch {
      setLeadState('error');
    }
  };

  const answerFr = (id: string, value: string) => {
    setFrAnswers((a) => ({ ...a, [id]: value }));
    setFrStep((n) => n + 1);
  };

  const submitFr = async () => {
    const name = frForm.name.trim();
    const email = frForm.email.trim();
    if (name.length < 2 || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email) || !frConsent) {
      setFrHint(t('scFrRequired'));
      return;
    }
    setFrHint(null);
    setFrState('sending');

    // Answers are keyed by their English question text so the note that lands
    // in Close reads the same whichever language the visitor used.
    const answers: Record<string, string> = {};
    FR_QUESTIONS.forEach((q) => {
      if (frAnswers[q.id]) answers[q.id] = frAnswers[q.id];
    });

    try {
      await joinFractionalWaitlist({
        name,
        email,
        phone: frForm.phone.trim(),
        note: frForm.note.trim(),
        answers,
        locale: language,
      });
      setFrState('done');
    } catch {
      setFrState('idle');
      setFrHint(t('scFrError'));
    }
  };

  const loadListings = useCallback(() => {
    setLoadState('loading');
    fetchSellListings(100)
      .then(({ listings: rows, total: count }) => {
        setListings(rows);
        setTotal(count);
        setLoadState('ready');
      })
      .catch(() => setLoadState('error'));
  }, []);

  useEffect(loadListings, [loadListings]);

  const filtered = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filterKey) || FILTERS[0];
    return listings.filter(f.test);
  }, [listings, filterKey]);

  const shown = filtered.slice(0, visibleCount);

  const bestDiscount = useMemo(
    () => listings.reduce((max, l) => (l.discountPercent && l.discountPercent > max ? l.discountPercent : max), 0),
    [listings],
  );

  const pickFilter = (key: string) => {
    setFilterKey(key);
    setVisibleCount(UNITS_PAGE);
  };

  const switchTab = (next: Tab) => {
    setTab(next);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const closeOverlay = useCallback(() => {
    setActiveId(null);
    setUnit(null);
    setFrame(null);
  }, []);

  // Escape closes whatever is raised; the page behind it shouldn't scroll.
  useEffect(() => {
    if (!overlayOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeOverlay(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [overlayOpen, closeOverlay]);

  // Sections rise in as they scroll into view. Additive only: without the
  // observer, or with reduced motion, CSS leaves everything visible.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const targets = Array.from(document.querySelectorAll('.sc-reveal:not(.sc-reveal--in)'));
    targets.forEach((el) => el.classList.add('sc-reveal--armed'));

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('sc-reveal--in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [tab, stageIdx, loadState, shown.length]);

  const enquireService = (svc: ServiceItem) => {
    const name = de ? svc.titleDe : svc.title;
    openWhatsApp(de
      ? `Hallo Asset Desk, ich interessiere mich für: ${name}.`
      : `Hello Asset Desk, I'd like to know more about: ${name}.`);
  };

  const enquireUnit = (l: SellListing) => {
    const label = [l.title, l.location].filter(Boolean).join(', ') || l.id;
    openWhatsApp(de
      ? `Hallo Asset Desk, ich interessiere mich für diese Einheit: ${label}.`
      : `Hello Asset Desk, I'm interested in this unit: ${label}.`);
  };

  const badges = (svc: ServiceItem) => (
    <>{svc.comingSoon && <em className="sc-badge">{t('scComingSoon')}</em>}</>
  );

  const bedLabel = (n: number | null) =>
    n == null ? null : n === 0 ? t('scStudio') : `${n} ${t(n === 1 ? 'scBed' : 'scBeds')}`;

  const stage = chapters[Math.min(stageIdx, chapters.length - 1)];

  return (
    <div className="sc-screen">
      <div className="sc-topbar">
        <div className="sc-nav">
          <button className="sc-back" onClick={onClose}>
            <ArrowLeft size={15} strokeWidth={2} />
            <span>{t('scBack')}</span>
          </button>

          {/* The real wordmark, not typeset text. It is black type with a gold
              accent, made for light grounds — which is what this nav bar is. */}
          <img src="/asset-desk-wordmark.png" alt="Asset Desk" className="sc-logo" />


          {/* EN / DE. The context persists the choice and re-renders the
              whole tree, so this one control switches every tab. */}
          <div className="sc-lang" role="group" aria-label={t('scLanguage')}>
            {(['en', 'de'] as const).map((code) => (
              <button
                key={code}
                type="button"
                lang={code}
                className={language === code ? 'is-on' : undefined}
                aria-pressed={language === code}
                onClick={() => setLanguage(code)}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>

          <button className="sc-nav-cta" onClick={() => { void openCalendly(); }}>
            {t('scBookCall')}
          </button>
        </div>
      </div>

      {/* Primary navigation, above the hero. It is sticky, but sticky only
          engages once an element is reached - sitting after the hero meant a
          full skyline had to be scrolled past before the tabs existed on
          screen at all. */}
      <nav className="sc-tabs" aria-label={t('scEyebrow')}>
        <div className="sc-tabs-inner">
          <div className="sc-tabrow">
            {TABS.map((x) => {
              const on = tab === x.key;
              return (
                <button
                  key={x.key}
                  className={`sc-tab${on ? ' sc-tab--on' : ''}`}
                  onClick={() => switchTab(x.key)}
                  aria-current={on}
                >
                  <b>{t(x.labelKey)}</b>
                  <span>{t(x.capKey)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Hero. The skyline plate is a CSS background, so if the asset is
          missing the gradient simply shows through instead of a broken box.
          Hidden on the Antavio, Home and Property Management tabs: each carries
          its own hero, and two stacked heroes read as a mistake. */}
      {tab !== 'fractional' && tab !== 'home' && tab !== 'pm' && (
      <header className="sc-hero">
        <div
          className="sc-hero-plate"
          aria-hidden="true"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="sc-glow sc-glow--top" aria-hidden="true" />

        <div className="sc-hero-inner">
          <p className="sc-eyebrow">{t('scHeroEyebrow')}</p>
          <h1 className="sc-hero-title">
            {t('scHeroTitleA')}
            <em>{t('scHeroTitleB')}</em>
          </h1>
          <p className="sc-hero-sub">{t('scIntro')}</p>

          <div className="sc-hero-actions">
            <button className="sc-cta sc-cta--primary" onClick={() => switchTab('units')}>
              <Building2 size={15} strokeWidth={2} />
              {t('scHeroCtaUnits')}
            </button>
            <button className="sc-cta" onClick={() => { void openCalendly(); }}>
              <CalendarDays size={15} strokeWidth={2} />
              {t('scBookCall')}
            </button>
          </div>

          <div className="sc-stats">
            <div className="sc-stat">
              <b>{loadState === 'ready' ? total : '—'}</b>
              <span>{t('scStatUnits')}</span>
            </div>
            <i aria-hidden="true" />
            <div className="sc-stat">
              <b>{ALL_SERVICES.length}</b>
              <span>{t('scStatServices')}</span>
            </div>
            <i aria-hidden="true" />
            <div className="sc-stat">
              <b>1</b>
              <span>{t('scStatTeam')}</span>
            </div>
          </div>
        </div>
      </header>
      )}

      {/* ---------------- UNITS ---------------- */}
      {/* ===== Home: the company overview =========================
          The pitch is the chain, not the products: purchase ->
          handover -> rental -> company -> Golden Visa -> exit, all
          in-house. The six stages reuse the Services tab's own
          chapter copy (scCh1..scCh6), so the two can never describe
          the journey differently.

          The figures are counted from the catalogue at render time
          rather than typed in, so they cannot go stale: the service
          count excludes anything still flagged comingSoon. */}
      {tab === 'home' && (
        <section className="sc-section sc-home">
          <div className="sc-home-hero">
            <div
              className="sc-home-hero-bg"
              style={{ backgroundImage: `url(${HOME_HERO})` }}
              role="img"
              aria-label={t('scHomeHeroAlt')}
            >
              {/* Ambient only: muted, looping, inline, and never a control
                  surface. Mounted only when heroFilmOk agreed it is worth the
                  bytes; the photograph underneath is the fallback either way. */}
              {heroFilmOk && (
                <video
                  className="sc-home-hero-video"
                  src={HOME_HERO_VIDEO}
                  poster={HOME_HERO}
                  muted
                  loop
                  autoPlay
                  playsInline
                  preload="auto"
                  aria-hidden="true"
                  tabIndex={-1}
                />
              )}
            </div>
            <div className="sc-wrap">
              <div className="sc-home-hero-inner">
                <p className="sc-sec-eyebrow">{t('scHomeEyebrow')}</p>
                <h2 className="sc-home-title">{t('scHomeTitle')}</h2>
                <p className="sc-home-lede">{t('scHomeLede')}</p>

                <div className="sc-actions">
                  <button className="sc-cta sc-cta--primary" onClick={() => { void openCalendly(); }}>
                    {t('scHomeCall')}
                  </button>
                  <button className="sc-cta" onClick={() => switchTab('services')}>
                    {t('scHomeSee')}
                  </button>
                </div>

                <div className="sc-home-proof">
                  <div>
                    <b>{liveServiceCount}</b>
                    <span>{t('scHomeProof1Sub')}</span>
                  </div>
                  <div>
                    <b>{CHAPTERS.length}</b>
                    <span>{t('scHomeProof2Sub')}</span>
                  </div>
                  <div>
                    <b>{t('scHomeProof3')}</b>
                    <span>{t('scHomeProof3Sub')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* The chain */}
          <div className="sc-wrap sc-home-body">
            <p className="sc-sec-eyebrow">{t('scHomeChainEyebrow')}</p>
            <h2 className="sc-sec-title">{t('scHomeChainTitle')}</h2>
            <p className="sc-lede">{t('scHomeChainLede')}</p>

            {/* The road IS the journey. Selecting a stop swaps the panel
                below rather than adding another screen of page - six stacked
                rows made one idea into a third of the tab. Tab semantics, so
                arrow keys work and the panel is announced. */}
            <div
              className="sc-road"
              ref={roadRef}
              role="tablist"
              aria-label={t('scHomeChainEyebrow')}
              onKeyDown={(e) => {
                const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
                if (!d) return;
                e.preventDefault();
                const next = (stop + d + CHAPTERS.length) % CHAPTERS.length;
                pickStop(next);
                document.getElementById(`sc-stop-${next}`)?.focus();
              }}
            >
              {/* The distance travelled: a solid gold line from the first
                  node to the active one, over the hairline the stops sit on.
                  Node centres are at (i + 0.5)/6 of the width, so the span
                  from the first to stop i is i/6. */}
              <span
                className="sc-road-fill"
                aria-hidden="true"
                style={{ width: `${(stop / CHAPTERS.length) * 100}%` }}
              />
              {CHAPTERS.map((c, i) => {
                const on = i === stop;
                return (
                  <div key={c.titleKey}>
                    <button
                      id={`sc-stop-${i}`}
                      role="tab"
                      aria-selected={on}
                      aria-controls="sc-stop-panel"
                      tabIndex={on ? 0 : -1}
                      className={on ? 'is-on' : i < stop ? 'is-done' : undefined}
                      onClick={() => pickStop(i)}
                    >
                      <span className="sc-road-node" aria-hidden="true">{c.numeral}</span>
                      <span className="sc-road-label">{t(c.titleKey)}</span>
                    </button>
                  </div>
                );
              })}
            </div>

            {/* One panel, fixed height, so switching stops does not shift the
                page under the reader. */}
            <div className="sc-stop-panel" id="sc-stop-panel" role="tabpanel" aria-labelledby={`sc-stop-${stop}`}>
              {/* Numeral and title are one cell, so the left column tops out
                  level with the copy on the right instead of dropping a row
                  below it. */}
              <div className="sc-stop-head">
                <p className="sc-stop-num">{CHAPTERS[stop].numeral}</p>
                <h3 className="sc-stop-title">{t(CHAPTERS[stop].titleKey)}</h3>
              </div>
              <p className="sc-stop-lede">{t(CHAPTERS[stop].ledeKey)}</p>
              <ul className="sc-stop-svc">
                {CHAPTERS[stop].ids.map((id) => {
                  const svc = serviceById.get(id);
                  if (!svc) return null;
                  return (
                    <li key={id} className={svc.comingSoon ? 'is-soon' : undefined}>
                      {de ? svc.titleDe : svc.title}
                      {svc.comingSoon ? <i>{t('scHomeSoon')}</i> : null}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="sc-actions">
              <button className="sc-cta" onClick={() => switchTab('services')}>
                {t('scHomeExplore')}
              </button>
            </div>
          </div>

          {/* How it works, on film. Click to play: it carries no narration
              worth autoplaying over, and an autoplaying 10s cut with sound
              would be worse than a poster. */}
          <div className="sc-home-film">
            <div className="sc-wrap">
              <p className="sc-sec-eyebrow">{t('scHomeFilmEyebrow')}</p>
              <h2 className="sc-sec-title">{t('scHomeFilmTitle')}</h2>
              <p className="sc-lede">{t('scHomeFilmLede')}</p>

              <div className={`sc-home-film-frame${filmStarted ? ' is-playing' : ''}`}>
                <video
                  ref={filmRef}
                  src={HOME_FILM}
                  poster={HOME_HERO}
                  muted
                  loop
                  autoPlay
                  controls={filmStarted}
                  playsInline
                  preload="auto"
                  onPlay={() => setFilmStarted(true)}
                  onTimeUpdate={onFilmTime}
                />

                {/* The cut carries no narration, so the stage is named here as
                    its shot plays. Keyed on the shot index so each caption
                    remounts and fades in. aria-live keeps a screen reader in
                    step; pointer-events are off in CSS so it never blocks the
                    controls. */}
                {filmStarted && (
                  <p
                    className="sc-home-film-cap"
                    key={filmShot}
                    aria-live="polite"
                  >
                    <span className="sc-home-film-cap-txt">{t(`scHomeBeat${filmShot + 1}`)}</span>
                  </p>
                )}

                {!filmStarted && (
                  <button
                    className="sc-home-film-play"
                    onClick={() => filmRef.current?.play()}
                    aria-label={t('scHomeFilmPlay')}
                  >
                    <span><Play size={24} strokeWidth={2} /></span>
                    {t('scHomeFilmPlay')}
                  </button>
                )}
              </div>

              {/* The same six stages as text, for anyone who will not or
                  cannot watch a video. The film is an illustration of the
                  chain, never the only place it is stated. */}
              <ol className="sc-home-film-list">
                {CHAPTERS.map((c) => (
                  <li key={c.titleKey}>
                    <b>{c.numeral}</b>
                    <span>{t(c.titleKey)}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Why a family would want to be here, not only own here. The rest
              of the page is empty rooms and paperwork; this is the warm
              moment, so it is full-bleed and gets room to breathe. */}
          <div className="sc-home-fam">
            <div
              className="sc-home-fam-bg"
              style={{ backgroundImage: `url(${HOME_FAM_HERO})` }}
              role="img"
              aria-label={t('scHomeFamHeroAlt')}
            />
            <div className="sc-wrap">
              <div className="sc-home-fam-inner">
                <p className="sc-sec-eyebrow">{t('scHomeFamEyebrow')}</p>
                <h2 className="sc-home-fam-title">{t('scHomeFamTitle')}</h2>
                <p className="sc-home-fam-lede">{t('scHomeFamLede')}</p>
                <div className="sc-actions">
                  <button className="sc-cta sc-cta--primary" onClick={() => switchTab('journey')}>
                    {t('scHomeFamCta')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="sc-wrap sc-home-body">
            <div className="sc-home-fam-grid">
              {HOME_FAM.map((x) => (
                <div key={x.titleKey}>
                  <img src={x.img} alt={t(x.altKey)} loading="lazy" />
                  <h3>{t(x.titleKey)}</h3>
                  <p>{t(x.bodyKey)}</p>
                </div>
              ))}
            </div>
            {/* Eligibility is not ours to promise. */}
            <p className="sc-credit">{t('scHomeFamNote')}</p>
          </div>

          {/* Who we are. The claim that principals invest alongside clients
              carries its disclosure directly underneath, because that is the
              whole reason for making the claim. */}
          <div className="sc-wrap sc-home-body">
            <div className="sc-home-who">
              <div>
                <p className="sc-sec-eyebrow">{t('scHomeWhoEyebrow')}</p>
                <h2 className="sc-sec-title">{t('scHomeWhoTitle')}</h2>
                <p className="sc-lede">{t('scHomeWhoLede')}</p>
                <ul className="sc-home-who-points">
                  <li>{t('scHomeWhoPoint1')}</li>
                  <li>{t('scHomeWhoPoint2')}</li>
                  <li>{t('scHomeWhoPoint3')}</li>
                </ul>
                <div className="sc-actions">
                  <button className="sc-cta" onClick={() => { void openCalendly(); }}>
                    {t('scHomeWhoCta')}
                  </button>
                </div>
              </div>

              <ul className="sc-home-team">
                {HOME_TEAM.map((m) => (
                  <li key={m.name}>
                    <img src={m.img} alt={m.name} loading="lazy" />
                    <p><b>{m.name}</b> &mdash; {t(m.roleKey)}</p>
                  </li>
                ))}
              </ul>
            </div>
            <p className="sc-credit">{t('scHomeWhoNote')}</p>
          </div>

          {/* How a developer gets in. Directly under "who we are" because it
              is the same claim made specific: "vetted listings only" is a
              slogan until the checks behind it are named. Ink ground, so it
              lands as its own statement between the paper team block and the
              white testimonial band.

              Attributed to the committee, not to a person - nobody has put
              their own name on this process, and a borrowed credential would
              be worse than none.

              Both figures are counted, never typed: the first from the logo
              wall this page already shows, the second from the live listings
              feed, so neither claim can outrun the inventory. */}
          <div className="sc-home-gate">
            <div className="sc-wrap">
              <p className="sc-sec-eyebrow">{t('scGateEyebrow')}</p>
              <h2 className="sc-sec-title">{t('scGateTitle')}</h2>
              <p className="sc-lede">{t('scGateLede')}</p>

              <ol className="sc-gate-list">
                {HOME_GATE.map((g) => (
                  <li key={g.n}>
                    {/* Decorative: the list is already ordered, and a screen
                        reader announcing "01" before the heading adds noise. */}
                    <b aria-hidden="true">{g.n}</b>
                    <h3>{t(g.titleKey)}</h3>
                    <p>{t(g.bodyKey)}</p>
                  </li>
                ))}
              </ol>

              {/* The refusal. Without it the section reads as "vetted, so
                  safe", which is not what any of the four checks proves. */}
              <p className="sc-gate-verdict">{t('scGateVerdict')}</p>

              <div className="sc-gate-proof">
                <div>
                  <b>{DEVELOPERS.length}</b>
                  <span>{t('scGateStat1Sub')}</span>
                </div>
                {/* Only once the feed has answered - a bare 0 while loading
                    would say something false about the inventory. */}
                {loadState === 'ready' && total > 0 && (
                  <div>
                    <b>{total}</b>
                    <span>{t('scGateStat2Sub')}</span>
                  </div>
                )}
              </div>

              <div className="sc-actions">
                <button className="sc-cta sc-cta--primary" onClick={() => switchTab('units')}>
                  {t('scGateCta')}
                </button>
                <button className="sc-cta" onClick={() => { void openCalendly(); }}>
                  {t('scGateAsk')}
                </button>
              </div>

              <p className="sc-credit">{t('scGateNote')}</p>
            </div>
          </div>

          {/* Investors who trust us - social proof belongs in front of the
              people who have not signed up yet, not behind the login. */}
          <div className="sc-fr-white">
          <div className="sc-wrap sc-home-body">
            <p className="sc-sec-eyebrow">{t('scHomeVoicesEyebrow')}</p>
            <h2 className="sc-sec-title">{t('ipInvestorsWhoTrust')}</h2>

            <div className="sc-voices" ref={voicesRef}>
              {HOME_VOICES.map((v) => (
                <figure className="sc-voice" key={v.id}>
                  <div className="sc-voice-video">
                    {voicesLive && (
                      <iframe
                        src={`https://fast.wistia.net/embed/iframe/${v.wistia}?autoPlay=true&muted=true&playbar=true&playsinline=true&endVideoBehavior=loop`}
                        title={v.name}
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                      />
                    )}
                  </div>
                  <figcaption>
                    <p className="sc-voice-quote">{t(v.quoteKey)}</p>
                    <p className="sc-voice-name">{v.name}</p>
                    <p className="sc-voice-title">{t(v.titleKey)}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
            <p className="sc-credit">{t('scHomeVoicesNote')}</p>
          </div>
          </div>

          {/* Five ways in */}
          <div className="sc-wrap sc-home-body">
            <p className="sc-sec-eyebrow">{t('scHomeInsideEyebrow')}</p>
            <h2 className="sc-sec-title">{t('scHomeInsideTitle')}</h2>

            <div className="sc-home-inside">
              {HOME_WAYS.map((w) => {
                const meta = TABS.find((x) => x.key === w.key);
                return (
                  <button className="sc-home-card" key={w.key} onClick={() => switchTab(w.key)}>
                    <b>{t(meta ? meta.labelKey : '')}</b>
                    <i>{t(meta ? meta.capKey : '')}</i>
                    <p>{t(w.descKey)}</p>
                    <em>{t('scViewAllShort')}</em>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Closing band */}
          <div className="sc-home-close">
            <div className="sc-wrap">
              <p className="sc-sec-eyebrow">{t('scHomeCloseEyebrow')}</p>
              <h2 className="sc-sec-title">{t('scHomeCloseTitle')}</h2>
              <p className="sc-lede">{t('scHomeCloseLede')}</p>
              <div className="sc-actions">
                <button className="sc-cta sc-cta--primary" onClick={() => { void openCalendly(); }}>
                  {t('scHomeCall')}
                </button>
                {/* Was openWhatsApp() with no argument, which put the literal
                    text "undefined" into the prospect's message box. */}
                <button
                  className="sc-cta"
                  onClick={() => openWhatsApp(de
                    ? 'Hallo Asset Desk, ich habe eine Frage zu Ihren Leistungen.'
                    : 'Hello Asset Desk, I have a question about your services.')}
                >
                  {t('scHomeWhats')}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {tab === 'units' && (
        <section className="sc-section">
          <div className="sc-wrap">
            <p className="sc-sec-eyebrow">{t('scForSaleEyebrow')}</p>
            <h2 className="sc-sec-title">{t('scForSaleTitle')}</h2>
            <p className="sc-lede">{t('scForSaleLede')}</p>

            <ul className="sc-trust">
              <li>{t('scTrust1')}</li>
              <li>{t('scTrust2')}</li>
              <li>{t('scTrust3')}</li>
            </ul>
          </div>

          {loadState === 'ready' && listings.length > 0 && (
            <div className="sc-filters-wrap">
              <div className="sc-filters">
                {FILTERS.map((f) => {
                  const n = listings.filter(f.test).length;
                  if (n === 0 && f.key !== 'all') return null;
                  return (
                    <button
                      key={f.key}
                      className={`sc-chip${filterKey === f.key ? ' sc-chip--on' : ''}`}
                      onClick={() => pickFilter(f.key)}
                    >
                      {t(f.labelKey)}<i>{n}</i>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {loadState === 'loading' && (
            <div className="sc-wrap">
              <div className="sc-unit-grid">
                {[0, 1, 2, 3].map((i) => <div className="sc-unit-skel" key={i} />)}
              </div>
            </div>
          )}

          {loadState === 'error' && (
            <div className="sc-wrap">
              <div className="sc-notice">
                <p>{t('scUnitsError')}</p>
                <button className="sc-cta" onClick={loadListings}>{t('scRetry')}</button>
              </div>
            </div>
          )}

          {loadState === 'ready' && listings.length === 0 && (
            <div className="sc-wrap">
              <div className="sc-notice"><p>{t('scUnitsEmpty')}</p></div>
            </div>
          )}

          {loadState === 'ready' && listings.length > 0 && (
            <div className="sc-wrap">
              {shown.length === 0 ? (
                <div className="sc-notice"><p>{t('scNoMatch')}</p></div>
              ) : (
                <>
                  <div className="sc-unit-grid">
                    {shown.map((l) => {
                      const price = formatAed(l.askingPrice);
                      return (
                        <button className="sc-unit sc-reveal" key={l.id} onClick={() => openUnit(l)}>
                          <span className="sc-unit-photo">
                            <img src={l.photoUrl || ''} alt={l.title || 'Unit'} loading="lazy" />
                            <span className="sc-unit-scrim" aria-hidden="true" />
                            {l.isDistress && l.discountPercent != null && (
                              <span className="sc-unit-flag">
                                {l.discountPercent}% {t('scBelowMarket')}
                              </span>
                            )}
                            {price && <span className="sc-unit-price">{price}</span>}
                          </span>

                          <span className="sc-unit-body">
                            <span className="sc-unit-name">{l.title || t('scUntitledUnit')}</span>
                            {l.location && (
                              <span className="sc-unit-loc">
                                <MapPin size={11} strokeWidth={2} />{l.location}
                              </span>
                            )}
                            <span className="sc-unit-specs">
                              {bedLabel(l.bedrooms) && (
                                <span><BedDouble size={12} strokeWidth={2} />{bedLabel(l.bedrooms)}</span>
                              )}
                              {l.sizeSqft != null && (
                                <span><Ruler size={12} strokeWidth={2} />{l.sizeSqft.toLocaleString('en-AE')} {t('scSqft')}</span>
                              )}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {shown.length < filtered.length && (
                    <div className="sc-more-wrap">
                      <button
                        className="sc-cta sc-more"
                        onClick={() => setVisibleCount((v) => v + UNITS_PAGE)}
                      >
                        {t('scViewAll').replace('{n}', String(filtered.length))}
                      </button>
                    </div>
                  )}

                  {shown.length >= filtered.length && (
                    <p className="sc-unit-more">
                      {t('scShowingUnits')
                        .replace('{shown}', String(shown.length))
                        .replace('{total}', String(filterKey === 'all' ? total : filtered.length))}
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </section>
      )}

      {/* ---------------- ANTAVIO — FRACTIONAL OWNERSHIP ----------------
          Structure and copy follow antavio-c7405.web.app. Two things from
          that page are deliberately NOT reproduced:

            - the BaFin / MiFID II / "EU-regulated security structure" claims.
              The source marks its own regulatory wording as an unconfirmed
              placeholder, and the licence is not in place, so asserting
              supervision here would be a claim we cannot stand behind.
            - the SAMANA Ocean Crest render. We do not hold that asset, and an
              AI-generated substitute for a real, named building would
              misrepresent it.

          Everything is future-tense and the panel says plainly that no offer
          is being made. Do not soften that without legal sign-off. */}
      {tab === 'fractional' && (
        <section className="sc-section sc-fr" ref={frRootRef}>
          {/* Hero */}
          <div className="sc-fr-hero">
            <div
              className="sc-fr-hero-bg"
              style={{ backgroundImage: `url(${HERO_IMAGE})` }}
              aria-hidden="true"
            />
            <div className="sc-wrap sc-fr-hero-inner">
              {/* Text stacks in its own column; the share grid is the flex
                  sibling. Without this wrapper the headline, sub and button
                  become flex siblings themselves and spread across the row. */}
              <div className="sc-fr-hero-text">
              <p className="sc-fr-eyebrow">{t('scFrEyebrow')}</p>
              <h2 className="sc-fr-title">
                {t('scFrTitleA')}
                {/* Second line is white up to the figure; only the price is
                    gold, as on the deployed page. */}
                <span className="sc-fr-title-b">
                  {t('scFrTitleB')} <em>{t('scFrTitleC')}</em>
                </span>
              </h2>
              <p className="sc-fr-lede">{t('scFrLede')}</p>
              <div className="sc-actions">
                <button className="sc-cta sc-cta--primary" onClick={scrollToForm}>
                  {t('scFrJoin')}
                </button>
              </div>
              </div>

            {/* Share grid, right of the headline - the signature element.
                Four filled of twenty-four, the same "one property, many
                shares" device as the concept section. It assembles on load;
                --i drives the per-cell stagger. The brief asks for it to be
                exposed as a single labelled image, not hidden. */}
            <div className="sc-fr-herogrid" role="img" aria-label={t('scFrHeroGridAria')}>
              <div className="sc-fr-herogrid-cells" aria-hidden="true">
                {Array.from({ length: 56 }, (_, i) => (
                  <span
                    key={i}
                    className={i < 4 ? 'is-mine' : undefined}
                    style={{ ['--i' as string]: i } as React.CSSProperties}
                  />
                ))}
              </div>
              <p>{t('scFrOneProperty')}</p>
            </div>
            </div>
          </div>

          {/* Figures band */}
          <div className="sc-fr-stats">
            <div className="sc-wrap sc-fr-stats-inner">
              {[
                { n: 'scFrStat1', s: 'scFrStat1Sub' },
                { n: 'scFrStat2', s: 'scFrStat2Sub' },
                { n: 'scFrStat3', s: 'scFrStat3Sub' },
                { n: 'scFrStat4', s: 'scFrStat4Sub' },
              ].map((x) => (
                <div className="sc-fr-stat" key={x.n}>
                  <CountUpFigure value={t(x.n)} />
                  <span>{t(x.s)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="sc-fr-body">
          <div className="sc-wrap">
            {/* Licence notice — the most important block on the page */}
            <div className="sc-flag-note sc-fr-notice">
              <Info size={15} strokeWidth={2} />
              <div>
                <b>{t('scFrNoticeTitle')}</b>
                <p>{t('scFrNotice')}</p>
              </div>
            </div>

            {/* Mission */}
            <p className="sc-sec-eyebrow sc-eyebrow--spaced">{t('scFrMissionEyebrow')}</p>
            <p className="sc-fr-mission">{t('scFrMission')}</p>

          </div>
          </div>

          {/* Concept — navy in the built output, not paper */}
          <div className="sc-fr-navy-deep">
          <div className="sc-wrap">
            <p className="sc-sec-eyebrow">{t('scFrConceptEyebrow')}</p>
            <h3 className="sc-stage-title">{t('scFrConceptTitle')}</h3>
            {/* The concept motion from components/FractionGrowth.tsx: a 6s
                loop where the grid assembles from the centre outward, one cell
                ignites gold, then the whole grid lifts and grows. 6x4 cells,
                the owned share at index 8 (row 1, col 2) as in the original.
                Per-cell delay is the original's 0.06 + dist * 0.26 of the
                timeline, in ms. */}
            <div className="sc-fr-fg" role="img" aria-label={t('scFrGridAria')}>
              <div className="sc-fr-fg-inner">
                <div className="sc-fr-fg-grid" aria-hidden="true">
                  {Array.from({ length: 24 }, (_, i) => {
                    const col = i % 6;
                    const row = Math.floor(i / 6);
                    const cx = 2.5;
                    const cy = 1.5;
                    const maxD = Math.sqrt(cx * cx + cy * cy);
                    const dist = Math.sqrt((col - cx) ** 2 + (row - cy) ** 2) / maxD;
                    const mine = i === 8;
                    return (
                      <span
                        key={i}
                        className={mine ? 'is-mine' : undefined}
                        style={{ ['--d' as string]: Math.round((0.06 + dist * 0.26) * 6000) } as React.CSSProperties}
                      >
                        {mine ? <i /> : null}
                      </span>
                    );
                  })}
                </div>
              </div>
              <p className="sc-fr-fg-tag">
                <b>1 / 24</b> {t('scFrStakeCap')}
              </p>
            </div>
            <p className="sc-lede">{t('scFrConcept')}</p>
          </div>
          </div>

          {/* How it works — paper, with white cards */}
          <div className="sc-fr-body">
          <div className="sc-wrap">
            <p className="sc-sec-eyebrow">{t('scFrHowEyebrow')}</p>
            <h3 className="sc-stage-title">{t('scFrHowTitle')}</h3>
            <div className="sc-fr-steps">
              {[
                { c: 'scFrHow1Cap', t: 'scFrHow1Title', d: 'scFrHow1', icon: Search },
                { c: 'scFrHow2Cap', t: 'scFrHow2Title', d: 'scFrHow2', icon: Wallet },
                { c: 'scFrHow3Cap', t: 'scFrHow3Title', d: 'scFrHow3', icon: Activity },
                { c: 'scFrHow4Cap', t: 'scFrHow4Title', d: 'scFrHow4', icon: TrendingUp },
              ].map((x, i) => {
                const Icon = x.icon;
                return (
                  <div className="sc-fr-stepcard" key={x.t}>
                    <span className="sc-tile sc-tile--sm"><Icon size={16} strokeWidth={2} /></span>
                    <p className="sc-fr-stepcap">
                      {String(i + 1).padStart(2, '0')} · {t(x.c)}
                    </p>
                    <b>{t(x.t)}</b>
                    <span>{t(x.d)}</span>
                  </div>
                );
              })}
            </div>
            <p className="sc-credit">{t('scFrHowNote')}</p>

          </div>
          </div>

          {/* First project — white band */}
          <div className="sc-fr-white">
          <div className="sc-wrap">
            <p className="sc-sec-eyebrow">{t('scFrProjectEyebrow')}</p>
            <h3 className="sc-stage-title">{t('scFrProjectName')}</h3>
            <p className="sc-lede">{t('scFrProjectLede')}</p>

            {/* Crest film. Muted, looping, plays inline — it is atmosphere, so
                it must never grab audio or block the page. preload="none" keeps
                a 19 MB file off the critical path until the user scrolls to it. */}
            <div className="sc-fr-video">
              <video
                src={ANT_CREST_VIDEO}
                poster={ANT_BALCONY_IMG}
                muted
                loop
                autoPlay
                playsInline
                preload="none"
              />
              {/* No overlay caption: the film has its own burned-in titles. */}
            </div>

            <div className="sc-fr-project">
              <img className="sc-fr-projimg" src={ANT_CREST_IMG} alt={t('scFrProjectName')} loading="lazy" />
              <div className="sc-fr-projbody">
              <p className="sc-fr-projloc">{t('scFrProjectLoc')}</p>
              <p className="sc-fr-projtitle">{t('scFrProjectTitle')}</p>
              <p className="sc-lede">{t('scFrProjectBody')}</p>

              <div className="sc-fr-projstats">
                {[
                  { n: 'scFrProjS1', s: 'scFrProjS1Sub' },
                  { n: 'scFrProjS2', s: 'scFrProjS2Sub' },
                  { n: 'scFrProjS3', s: 'scFrProjS3Sub' },
                  { n: 'scFrProjS4', s: 'scFrProjS4Sub' },
                ].map((x) => (
                  <div key={x.n}>
                    <b>{t(x.n)}</b>
                    <span>{t(x.s)}</span>
                  </div>
                ))}
              </div>

              {/* Four filled of fifty-six, at the deployed page's 14-column
                  geometry - the stake in this specific building. */}
              <p className="sc-fr-sharelabel">{t('scFrOneProperty')}</p>
              <div className="sc-fr-cardgrid" role="img" aria-label={t('scFrGridAria')}>
                {Array.from({ length: 56 }, (_, i) => (
                  <span key={i} className={i < 4 ? 'is-mine' : undefined} />
                ))}
              </div>
              <p className="sc-fr-illus">{t('scFrShareIllus')}</p>

              <ul className="sc-fr-amen">
                {['scFrAmen1', 'scFrAmen2', 'scFrAmen3', 'scFrAmen4', 'scFrAmen5', 'scFrAmen6']
                  .map((k) => <li key={k}>{t(k)}</li>)}
              </ul>

              <div className="sc-actions">
                <button className="sc-cta sc-cta--primary" onClick={scrollToForm}>
                  {t('scFrViewProject')}
                </button>
              </div>

              <p className="sc-credit">{t('scFrProjectNote')}</p>
              </div>
            </div>

          </div>
          </div>

          {/* The Antavio app — paper band with the intro film and the app still */}
          <div className="sc-fr-body">
          <div className="sc-wrap">
            <p className="sc-sec-eyebrow">{t('scFrAppEyebrow')}</p>
            <h3 className="sc-stage-title">{t('scFrAppTitle')}</h3>

            <div className="sc-fr-app">
              <div className="sc-fr-app-video">
                <video
                  ref={explainerRef}
                  src={de ? ANT_EXPLAINER_DE : ANT_EXPLAINER_EN}
                  controls
                  playsInline
                  preload="none"
                  poster={ANT_TEAM_IMG}
                  onPlay={() => setExplainerStarted(true)}
                />
                {/* Play affordance sits over the poster until the film starts,
                    the way the deployed page presents it. Once playing it is
                    removed so it never covers the control bar. */}
                {!explainerStarted && (
                  <button
                    className="sc-fr-play"
                    onClick={() => explainerRef.current?.play()}
                    aria-label={t('scFrAppWatch')}
                  >
                    <i><Play size={20} strokeWidth={2} /></i>
                    {t('scFrAppWatch')}
                  </button>
                )}
              </div>
              <img className="sc-fr-app-shot" src={ANT_APP_SVG} alt={t('scFrAppTitle')} loading="lazy" />
            </div>

            <ul className="sc-fr-appfeat">
              {[
                { icon: Activity, key: 'scFrApp1' },
                { icon: Gauge, key: 'scFrApp2' },
                { icon: Layers, key: 'scFrApp3' },
              ].map(({ icon: Icon, key }) => (
                <li key={key}>
                  <span className="sc-tile sc-tile--sm"><Icon size={16} strokeWidth={2} /></span>
                  <span>{t(key)}</span>
                </li>
              ))}
            </ul>

            <p className="sc-credit">{t('scFrMediaNote')}</p>
          </div>
          </div>

          {/* Why Antavio — white band */}
          <div className="sc-fr-white">
          <div className="sc-wrap sc-fr-left">
            <p className="sc-sec-eyebrow sc-eyebrow--spaced">{t('scFrWhyEyebrow')}</p>
            <h3 className="sc-stage-title">{t('scFrWhyTitle')}</h3>
            <div className="sc-fr-whygrid">
              <div>
                <p className="sc-lede">{t('scFrWhyLede')}</p>

                {/* The 100% / digital figure lives here, not in the hero band. */}
                <div className="sc-fr-whystats">
                  {[
                    { n: 'scFrWhyS1', s: 'scFrWhyS1Sub' },
                    { n: 'scFrWhyS2', s: 'scFrWhyS2Sub' },
                    { n: 'scFrWhyS3', s: 'scFrWhyS3Sub' },
                    { n: 'scFrWhyS4', s: 'scFrWhyS4Sub' },
                  ].map((x) => (
                    <div key={x.n}>
                      <b>{t(x.n)}</b>
                      <span>{t(x.s)}</span>
                    </div>
                  ))}
                </div>

                <ul className="sc-points">
                  {[
                    { icon: MapPin, key: 'scFrWhy1' },
                    { icon: Handshake, key: 'scFrWhy2' },
                    { icon: Gauge, key: 'scFrWhy3' },
                  ].map(({ icon: Icon, key }) => (
                    <li key={key}>
                      <span className="sc-tile sc-tile--sm"><Icon size={16} strokeWidth={2} /></span>
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <img className="sc-fr-whyimg" src={ANT_TEAM_IMG} alt={t('scFrTeamAlt')} loading="lazy" />
            </div>
          </div>
          </div>

          {/* Why investors choose us — paper band */}
          <div className="sc-fr-body">
          <div className="sc-wrap">
            <p className="sc-sec-eyebrow">{t('scFrChooseEyebrow')}</p>
            <h3 className="sc-stage-title">{t('scFrChooseTitle')}</h3>
            <div className="sc-fr-choose">
              {['scFrChoose1', 'scFrChoose2', 'scFrChoose3', 'scFrChoose4', 'scFrChoose5', 'scFrChoose6']
                .map((k) => (
                  <div key={k}>
                    <Check size={14} strokeWidth={2.4} />
                    <span>{t(k)}</span>
                  </div>
                ))}
            </div>

          </div>
          </div>

          {/* Trust & security — a full navy band, as the brief prescribes for
              trust moments. */}
          <div className="sc-fr-trust-band">
          <div className="sc-wrap">
            <p className="sc-sec-eyebrow">{t('scFrTrustEyebrow')}</p>
            <h3 className="sc-stage-title">{t('scFrTrustTitle')}</h3>
            <div className="sc-value-grid sc-fr-trust">
              {[
                { icon: Building2, t: 'scFrTrust1Title', d: 'scFrTrust1' },
                { icon: FileText, t: 'scFrTrust2Title', d: 'scFrTrust2' },
                { icon: Info, t: 'scFrTrust3Title', d: 'scFrTrust3' },
              ].map(({ icon: Icon, t: title, d }) => (
                <div className="sc-value-item" key={title}>
                  <span className="sc-tile"><Icon size={21} strokeWidth={1.9} /></span>
                  <b>{t(title)}</b>
                  <p>{t(d)}</p>
                </div>
              ))}
            </div>

          </div>
          </div>

          {/* FAQ — white band, as in the built output */}
          <div className="sc-fr-white">
          <div className="sc-wrap">
            <p className="sc-sec-eyebrow">{t('scFrFaqEyebrow')}</p>
            <h3 className="sc-stage-title">{t('scFrFaqTitle')}</h3>
            <div className="sc-faq">
              {[1, 2, 3, 4, 5, 6].map((n) => {
                const open = faqOpen === n;
                return (
                  <div className={`sc-faq-item${open ? ' is-open' : ''}`} key={n}>
                    <button
                      className="sc-faq-q"
                      onClick={() => setFaqOpen(open ? null : n)}
                      aria-expanded={open}
                    >
                      <span>{t(`scFrQ${n}`)}</span>
                      {open ? <X size={17} strokeWidth={2} /> : <Plus size={17} strokeWidth={2} />}
                    </button>
                    {open && <p className="sc-faq-a">{t(`scFrA${n}`)}</p>}
                  </div>
                );
              })}
            </div>
          </div>
          </div>

          {/* Early access — gold band, as in the Antavio reference */}
          <div className="sc-fr-gold">
          <div className="sc-wrap">
            <div className="sc-lead sc-fr-form" ref={frFormRef}>
              {frState === 'done' ? (
                <div className="sc-lead-ok">
                  <CheckCircle2 size={18} strokeWidth={2} />
                  <div>
                    <p><b>{t('scFrDoneTitle')}</b></p>
                    <p>{t('scFrDone')}</p>
                  </div>
                </div>
              ) : frStep < FR_QUESTIONS.length ? (
                <>
                  <p className="sc-sec-eyebrow">{t('scFrCtaEyebrow')}</p>
                  <p className="sc-lead-title">{t('scFrCtaTitle')}</p>
                  <p className="sc-lead-sub">{t('scFrCtaBody')}</p>
                  <p className="sc-fr-step">
                    {t('scFrStep')
                      .replace('{n}', String(frStep + 1))
                      .replace('{total}', String(FR_QUESTIONS.length))}
                  </p>
                  <p className="sc-fr-question">{t(FR_QUESTIONS[frStep].titleKey)}</p>
                  <div className="sc-fr-options">
                    {FR_QUESTIONS[frStep].optionKeys.map((k) => (
                      <button
                        key={k}
                        className="sc-fr-option"
                        onClick={() => answerFr(FR_QUESTIONS[frStep].id, t(k))}
                      >
                        {t(k)}
                        <ArrowRight size={14} strokeWidth={2} />
                      </button>
                    ))}
                  </div>
                  {frStep > 0 && (
                    <button className="sc-fr-back" onClick={() => setFrStep((n) => n - 1)}>
                      <ArrowLeft size={13} strokeWidth={2} />{t('scFrBack')}
                    </button>
                  )}
                </>
              ) : (
                <>
                  <p className="sc-fr-step">{t('scFrLastStep')}</p>
                  <p className="sc-lead-title">{t('scFrJoin')}</p>

                  <div className="sc-lead-form">
                    <input
                      type="text" autoComplete="name" placeholder={t('scLeadName')}
                      value={frForm.name}
                      onChange={(e) => setFrForm({ ...frForm, name: e.target.value })}
                    />
                    <input
                      type="email" autoComplete="email" placeholder={t('scLeadEmail')}
                      value={frForm.email}
                      onChange={(e) => setFrForm({ ...frForm, email: e.target.value })}
                    />
                    <input
                      type="tel" autoComplete="tel" placeholder={t('scLeadPhone')}
                      value={frForm.phone}
                      onChange={(e) => setFrForm({ ...frForm, phone: e.target.value })}
                    />
                    <textarea
                      placeholder={t('scFrNote')}
                      value={frForm.note}
                      onChange={(e) => setFrForm({ ...frForm, note: e.target.value })}
                    />

                    <label className="sc-consent">
                      <input
                        type="checkbox"
                        checked={frConsent}
                        onChange={(e) => setFrConsent(e.target.checked)}
                      />
                      <span>{t('scFrConsent')}</span>
                    </label>

                    <button
                      className="sc-cta sc-cta--primary"
                      onClick={submitFr}
                      disabled={frState === 'sending'}
                    >
                      <Send size={14} strokeWidth={2} />
                      {frState === 'sending' ? t('scFrSubmitting') : t('scFrSubmit')}
                    </button>
                  </div>

                  {frHint && <p className="sc-lead-err">{frHint}</p>}
                  <button className="sc-fr-back" onClick={() => setFrStep((n) => n - 1)}>
                    <ArrowLeft size={13} strokeWidth={2} />{t('scFrBack')}
                  </button>
                  <p className="sc-lead-legal">{t('scLeadLegal')}</p>
                </>
              )}
            </div>

            <p className="sc-credit sc-fr-risk">{t('scFrRisk')}</p>
          </div>
          </div>
        </section>
      )}

      {/* ---------------- STAYS (Airbnb arm) ----------------
          Deliberately not a listings grid: the six apartments are still being
          furnished and there is no booking backend, so inventing bookable
          cards here would misrepresent them. Real facts, an enquiry route,
          and a plain statement that it isn't live yet. */}
      {tab === 'stays' && (
        <section className="sc-section sc-section--flush">
          <div className="sc-banner">
            <div
              className="sc-banner-img"
              style={{ backgroundImage: `url(${STAYS_IMAGE})` }}
              aria-hidden="true"
            />
            <div className="sc-banner-body">
              <p className="sc-sec-eyebrow">{t('scStaysEyebrow')}</p>
              <h2 className="sc-sec-title">{t('scStaysTitle')}</h2>
            </div>
          </div>

          <div className="sc-wrap">
            <p className="sc-lede">{t('scStaysLede')}</p>

            <p className="sc-sec-eyebrow sc-eyebrow--spaced">{t('scStaysOpen')}</p>

            <div className="sc-stay-list">
              {STAYS.map((stay) => (
                <div className="sc-stay" key={stay.id}>
                  <span className="sc-tile"><Umbrella size={21} strokeWidth={1.9} /></span>
                  <div className="sc-stay-text">
                    <b>{t(stay.nameKey)}</b>
                    <span>{t(stay.subKey)}</span>
                  </div>
                  <button
                    className="sc-cta sc-cta--primary sc-stay-btn"
                    onClick={() => setFrame({ url: bookingUrl(stay.propId), label: t(stay.nameKey) })}
                  >
                    <CalendarDays size={14} strokeWidth={2} />
                    {t('scStaysBook')}
                  </button>
                </div>
              ))}
            </div>

            <p className="sc-credit">{t('scStaysBookedVia')}</p>

            <div className="sc-figs">
              <div className="sc-fig">
                <b>6</b>
                <span>{t('scStaysStat1')}</span>
              </div>
              <div className="sc-fig">
                <b>11.3%</b>
                <span>{t('scStaysStat2')}</span>
              </div>
              <div className="sc-fig">
                <b>{t('scStaysStat3Val')}</b>
                <span>{t('scStaysStat3')}</span>
              </div>
            </div>

            <ul className="sc-points">
              {[
                { icon: Sparkles, key: 'scStaysPoint1' },
                { icon: Waves, key: 'scStaysPoint2' },
                { icon: Umbrella, key: 'scStaysPoint3' },
                { icon: TrendingUp, key: 'scStaysPoint4' },
              ].map(({ icon: Icon, key }) => (
                <li key={key}>
                  <span className="sc-tile sc-tile--sm"><Icon size={16} strokeWidth={2} /></span>
                  <span>{t(key)}</span>
                </li>
              ))}
            </ul>


            <div className="sc-flag-note">
              <Info size={15} strokeWidth={2} />
              <div>
                <b>{t('scStaysMoreTitle')}</b>
                <p>{t('scStaysMore')}</p>
              </div>
            </div>

            <div className="sc-actions">
              <button
                className="sc-cta"
                onClick={() => openWhatsApp(de
                  ? 'Hallo Asset Desk, ich interessiere mich fuer einen Kurzaufenthalt in Ihren Wohnungen. Mein Wunschzeitraum: '
                  : 'Hello Asset Desk, I am interested in a short stay in your apartments. My preferred dates: ')}
              >
                <MessageCircle size={15} strokeWidth={2} />
                {t('scStaysCta')}
              </button>
            </div>

            <p className="sc-credit">{t('scImageImpression')}</p>
          </div>
        </section>
      )}

      {/* ---------------- PROPERTY MANAGEMENT ----------------
          The pitch for the managed service. It sits behind its own tab rather
          than as a Services card because it is the one offering that continues
          for years after the sale, and it has its own phone line.

          Every enquiry on this page goes to WHATSAPP_PM_NUMBER, not the
          general number - the team that runs the buildings answers it. */}
      {tab === 'pm' && (
        <section className="sc-section sc-section--flush sc-pm">
          {/* Own banner rather than the shared skyline plate, like Stays and
              Journey: the tab opens on the thing being looked after. */}
          <div className="sc-banner sc-pm-banner">
            <div
              className="sc-banner-img"
              style={{ backgroundImage: `url(${PM_HERO})` }}
              aria-hidden="true"
            >
              {/* Ambient only: muted, looping, inline, never a control surface.
                  Gated by the same heroFilmOk test the Home hero uses - wide
                  screen, no reduced-motion preference, not on a metered or 2G/3G
                  connection - and the photograph underneath is the fallback. */}
              {heroFilmOk && (
                <video
                  className="sc-pm-banner-video"
                  src={PM_HERO_VIDEO}
                  poster={PM_HERO}
                  muted
                  loop
                  autoPlay
                  playsInline
                  preload="auto"
                  aria-hidden="true"
                  tabIndex={-1}
                />
              )}
            </div>
            <div className="sc-banner-body">
              <p className="sc-sec-eyebrow">{t('scPmEyebrow')}</p>
              <h2 className="sc-sec-title">{t('scPmTitle')}</h2>
            </div>
          </div>

          <div className="sc-wrap">
            <p className="sc-lede sc-pm-lede">{t('scPmLede')}</p>

            {/* The five stages, in the order they happen. Same numerals as the
                Home tab's journey, because it is the same house style.

                Paired with a tall photograph from ~1000px up: the ladder only
                ever fills the left half of a wide page, and the empty right
                half made the section read as unfinished. The image is sticky,
                so it holds the eye while the five stages scroll past it. */}
            <div className="sc-pm-stage-row">
              {/* Tab semantics, like the Home tab's road: arrow keys move
                  between stages and the panel is announced when it changes. */}
              <ol
                className="sc-pm-stages"
                role="tablist"
                aria-label={t('scPmStagesLabel')}
                onKeyDown={(e) => {
                  const d = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1
                    : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 0;
                  if (!d) return;
                  e.preventDefault();
                  const next = (pmStage + d + PM_STAGES.length) % PM_STAGES.length;
                  setPmStage(next);
                  document.getElementById(`sc-pm-stage-${next}`)?.focus();
                }}
              >
                {PM_STAGES.map((s, i) => {
                  const on = i === pmStage;
                  return (
                    <li key={s.n} className={on ? 'is-on' : undefined}>
                      <button
                        id={`sc-pm-stage-${i}`}
                        role="tab"
                        aria-selected={on}
                        aria-controls="sc-pm-stage-panel"
                        tabIndex={on ? 0 : -1}
                        onClick={() => setPmStage(i)}
                      >
                        <b aria-hidden="true">{s.n}</b>
                        <span>
                          <h3>{t(s.titleKey)}</h3>
                          <p>{t(s.bodyKey)}</p>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>

              {/* Keyed on the stage so the screen remounts and cross-fades
                  instead of swapping with a flash of the previous frame. */}
              <figure
                className="sc-pm-stage-figure"
                id="sc-pm-stage-panel"
                role="tabpanel"
                aria-labelledby={`sc-pm-stage-${pmStage}`}
              >
                <img
                  key={PM_STAGES[pmStage].shot}
                  src={PM_STAGES[pmStage].shot}
                  alt={t(PM_STAGES[pmStage].altKey)}
                />
                <figcaption>{t(PM_STAGES[pmStage].capKey)}</figcaption>
              </figure>
            </div>

            {/* Everything the fee actually buys. Three groups rather than one
                long list, because the three answer different worries: will it
                earn, will it be looked after, and will it keep bothering me. */}
            <p className="sc-sec-eyebrow sc-eyebrow--spaced">{t('scPmGetEyebrow')}</p>
            <h2 className="sc-sec-title">{t('scPmGetTitle')}</h2>
            <p className="sc-lede">{t('scPmGetLede')}</p>

            <div className="sc-pm-benefits">
              {PM_BENEFITS.map((g) => (
                <div className="sc-pm-group" key={g.titleKey}>
                  <h3>{t(g.titleKey)}</h3>
                  <ul>
                    {g.items.map((it) => (
                      <li key={it.titleKey}>
                        <Check size={15} strokeWidth={2.6} aria-hidden="true" />
                        <div>
                          <b>{t(it.titleKey)}</b>
                          <p>{t(it.bodyKey)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <p className="sc-sec-eyebrow sc-eyebrow--spaced">{t('scPmScreenEyebrow')}</p>
            <h2 className="sc-sec-title">{t('scPmScreenTitle')}</h2>
            <p className="sc-lede">{t('scPmScreenLede')}</p>

            {/* Three screens rather than one. A single frame reads as a mockup;
                three read as a product, and they let the page show inspection
                reports and the expense breakdown, which are the two things a
                sceptical landlord actually wants to see. */}
            <div className="sc-pm-phones">
              {PM_SCREENS.map((s) => (
                <figure key={s.src}>
                  <img src={s.src} alt={t(s.altKey)} loading="lazy" />
                  <figcaption>{t(s.capKey)}</figcaption>
                </figure>
              ))}
            </div>

            {/* Same card device as the Units value band - gold tile, bold
                line, body - rather than a second one that means the same. */}
            <div className="sc-value-grid sc-pm-grid">
              {PM_SCREEN.map(({ icon: Icon, titleKey, bodyKey }) => (
                <div className="sc-value-item" key={titleKey}>
                  <span className="sc-tile"><Icon size={21} strokeWidth={1.9} /></span>
                  <b>{t(titleKey)}</b>
                  <p>{t(bodyKey)}</p>
                </div>
              ))}
            </div>

            {/* Drawn, not screenshotted, and said so - a real capture would
                carry a real owner's name, unit and rent. */}
            <p className="sc-credit sc-pm-credit">{t('scPmShotNote')}</p>

            {/* What the fee does not cover, stated here rather than discovered
                on an invoice. */}
            <div className="sc-notice sc-pm-notice">
              <p>{t('scPmNotice')}</p>
            </div>

          </div>

          {/* The close, on the keys photograph. Full-bleed and inverted, so the
              page ends on an image the way it began instead of trailing off in
              a grey rule. The photograph's dark side is on the right, which is
              where the copy sits. */}
          <div className="sc-pm-close">
            <div
              className="sc-pm-close-bg"
              style={{ backgroundImage: `url(${PM_KEYS})` }}
              role="img"
              aria-label={t('scPmKeysAlt')}
            />
            <div className="sc-wrap">
              <div className="sc-pm-close-inner">
                <p className="sc-sec-eyebrow">{t('scPmCloseEyebrow')}</p>
                <h2 className="sc-sec-title">{t('scPmCloseTitle')}</h2>
                <p className="sc-lede">{t('scPmCloseLede')}</p>

                <div className="sc-actions">
                  <button
                    className="sc-cta sc-cta--primary"
                    onClick={() => openWhatsApp(
                      de
                        ? 'Hallo Asset Desk, ich möchte mehr über Ihre Immobilienverwaltung erfahren.'
                        : 'Hello Asset Desk, I would like to know more about your property management.',
                      WHATSAPP_PM_NUMBER,
                    )}
                  >
                    <MessageCircle size={15} strokeWidth={2} />
                    {t('scPmWhats')}
                  </button>
                  <button className="sc-cta" onClick={() => { void openCalendly(); }}>
                    <CalendarDays size={15} strokeWidth={2} />
                    {t('scBookCall')}
                  </button>
                </div>

                {/* Shown in full, because a person deciding whether to trust you
                    with their building wants to see the number before they tap. */}
                <p className="sc-pm-number">
                  <span>{t('scPmNumberLabel')}</span>
                  <b>{PM_WHATSAPP_DISPLAY}</b>
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ---------------- JOURNEY ---------------- */}
      {tab === 'journey' && (
        <section className="sc-section sc-section--flush">
          <div className="sc-banner">
            <div
              className="sc-banner-img"
              style={{ backgroundImage: `url(${JOURNEY_IMAGE})` }}
              aria-hidden="true"
            />
            <div className="sc-banner-body">
              <p className="sc-sec-eyebrow">{t('scJourneyEyebrow')}</p>
              <h2 className="sc-sec-title">{t('scJourneyTitle')}</h2>
            </div>
          </div>

          <div className="sc-wrap">
            <p className="sc-lede">{t('scJourneyLede')}</p>

            <ul className="sc-points">
              {[
                { icon: Building2, key: 'scJourney1' },
                { icon: Gauge, key: 'scJourney2' },
                { icon: Handshake, key: 'scJourney3' },
                { icon: MapPin, key: 'scJourney4' },
              ].map(({ icon: Icon, key }) => (
                <li key={key}>
                  <span className="sc-tile sc-tile--sm"><Icon size={16} strokeWidth={2} /></span>
                  <span>{t(key)}</span>
                </li>
              ))}
            </ul>

            {/* The trip on film. Muted autoplay because browsers require it,
                but with controls and audio intact - this is the thing being
                sold, not wallpaper. If autoplay is declined the poster and a
                play button stand in. */}
            <div className="sc-journey-film">
              <p className="sc-sec-eyebrow">{t('scJourneyFilmEyebrow')}</p>
              <h3 className="sc-stage-title">{t('scJourneyFilmTitle')}</h3>
              <div className="sc-journey-film-frame">
                <video
                  ref={journeyFilmRef}
                  src={JOURNEY_FILM}
                  poster={JOURNEY_FILM_POSTER}
                  muted
                  loop
                  autoPlay
                  playsInline
                  controls
                  preload="metadata"
                  onPlay={() => setJourneyPlaying(true)}
                  onPause={() => setJourneyPlaying(false)}
                />
                {!journeyPlaying && (
                  <button
                    className="sc-journey-film-play"
                    onClick={() => journeyFilmRef.current?.play()}
                    aria-label={t('scJourneyFilmTitle')}
                  >
                    <span><Play size={24} strokeWidth={2} /></span>
                    {t('scJourneyFilmTitle')}
                  </button>
                )}
              </div>
              <p className="sc-journey-film-note">{t('scJourneyFilmNote')}</p>
            </div>

            <div className="sc-actions">
              <button
                className="sc-cta sc-cta--primary"
                onClick={() => setFrame({ url: JOURNEY_URL, label: t('scJourneyTitle') })}
              >
                <CalendarDays size={15} strokeWidth={2} />
                {t('scJourneyCta')}
              </button>
            </div>

            <p className="sc-credit">{t('scImageImpression')}</p>
          </div>
        </section>
      )}

      {/* ---------------- SERVICES ---------------- */}
      {tab === 'services' && (
        <section className="sc-section">
          <div className="sc-wrap">
            <p className="sc-sec-eyebrow">{t('scServicesEyebrow')}</p>
            <h2 className="sc-sec-title">{t('scServicesTitle')}</h2>
            <p className="sc-lede">{t('scServicesLede')}</p>
          </div>

          {/* One sub-tab per stage, so a stage's services are read on their
              own rather than as part of a 29-card wall. */}
          <div className="sc-filters-wrap">
            <div className="sc-filters">
              {chapters.map((ch, i) => (
                <button
                  key={ch.numeral}
                  className={`sc-chip${stageIdx === i ? ' sc-chip--on' : ''}`}
                  onClick={() => setStageIdx(i)}
                >
                  <b>{ch.numeral}</b>{t(ch.titleKey)}
                </button>
              ))}
            </div>
          </div>

          {stage && (
            <div className="sc-wrap">
              {/* No numeral repeated here — the active stage chip above
                  already carries it, and showing both read as a stutter. */}
              <h3 className="sc-stage-title">{t(stage.titleKey)}</h3>
              <p className="sc-lede">{t(stage.ledeKey)}</p>

              <div className="sc-grid">
                {stage.items.map((svc) => {
                  const docCount = (de ? svc.documentsDe : svc.documents).length;
                  return (
                    <button
                      className={`sc-svc sc-reveal${svc.comingSoon ? ' sc-svc--soon' : ''}`}
                      key={svc.id}
                      onClick={() => setActiveId(svc.id)}
                    >
                      <span className="sc-tile"><TileIcon icon={svc.icon} /></span>
                      <span className="sc-svc-title">
                        {de ? svc.titleDe : svc.title}
                        {badges(svc)}
                      </span>
                      <span className="sc-svc-desc">{de ? svc.descriptionDe : svc.description}</span>
                      <span className="sc-svc-foot">
                        <span className="sc-svc-docs">
                          {docCount > 0 && (
                            <>
                              <FileText size={11} strokeWidth={2} />
                              {docCount} {t(docCount === 1 ? 'scDocument' : 'scDocuments')}
                            </>
                          )}
                        </span>
                        <span className="sc-svc-go">
                          {t('scExplore')}
                          <ArrowRight size={13} strokeWidth={2.2} />
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Closing CTA, on every tab — the way out is always the same. */}
      {/* Units-tab only: these are the developers behind the units for sale, so
          the band has nothing to say under Stays, Journey or Services — and
          repeating it made all four tabs look the same below the fold. */}
      {tab === 'units' && (
      <section className="sc-devs sc-reveal">
        <div
          className="sc-devs-bg"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
          aria-hidden="true"
        />
        <div className="sc-wrap">
          <p className="sc-devs-title">{t('scDevsTitle')}</p>
          <ul className="sc-devs-grid">
            {DEVELOPERS.map((d) => (
              <li key={d.slug}>
                <img src={`/developers/${d.slug}.png`} alt={`${d.name} logo`} loading="lazy" />
              </li>
            ))}
          </ul>
        </div>
      </section>
      )}

      {/* Marketing band, also Units-only. The discount figure is computed from
          the live listings, so the claim can never outrun the inventory. */}
      {tab === 'units' && (
      <section className="sc-value sc-reveal">
        <div className="sc-wrap">
          <p className="sc-sec-eyebrow">{t('scValueEyebrow')}</p>
          <h2 className="sc-sec-title">{t('scValueTitle')}</h2>
          <p className="sc-lede">{t('scValueLede')}</p>

          <div className="sc-value-grid">
            {[
              {
                icon: TrendingUp,
                title: t('scValue1Title'),
                body: bestDiscount > 0
                  ? t('scValue1').replace('{n}', String(bestDiscount))
                  : t('scValue1Fallback'),
              },
              { icon: Handshake, title: t('scValue2Title'), body: t('scValue2') },
              { icon: Layers, title: t('scValue3Title'), body: t('scValue3').replace('{n}', String(ALL_SERVICES.length)) },
              { icon: Gauge, title: t('scValue4Title'), body: t('scValue4') },
            ].map(({ icon: Icon, title, body }) => (
              <div className="sc-value-item" key={title}>
                <span className="sc-tile"><Icon size={21} strokeWidth={1.9} /></span>
                <b>{title}</b>
                <p>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      <section className="sc-closing">
        <div className="sc-glow sc-glow--closing" aria-hidden="true" />
        <div className="sc-wrap">
          <p className="sc-closing-title">{t('scHelpTitle')}</p>
          <p className="sc-lede">{t('scHelpSub')}</p>
          <div className="sc-actions">
            <button
              className="sc-cta sc-cta--primary"
              onClick={() => openWhatsApp(de
                ? 'Hallo Asset Desk, ich habe eine Frage zu Ihren Leistungen.'
                : 'Hello Asset Desk, I have a question about your services.')}
            >
              <MessageCircle size={15} strokeWidth={2} />
              {t('scAskOnWhatsApp')}
            </button>
            <button className="sc-cta" onClick={() => { void openCalendly(); }}>
              <CalendarDays size={15} strokeWidth={2} />
              {t('scBookCall')}
            </button>
          </div>
        </div>
      </section>

      {/* Page footer. Reachable before any login, like the welcome screen's,
          and deliberately not worded as implied consent — versioned consent
          is captured at sign-up. */}
      <footer className="sc-foot">
        <div className="sc-wrap sc-foot-inner">
          <span className="sc-foot-mark">Asset Desk</span>
          <nav className="sc-foot-links" aria-label={t('rsLegal')}>
            <a href={TERMS_URL} onClick={openPolicy(TERMS_URL)} target="_blank" rel="noopener noreferrer">
              {t('rsTerms')}
            </a>
            <a href={PRIVACY_URL} onClick={openPolicy(PRIVACY_URL)} target="_blank" rel="noopener noreferrer">
              {t('rsPrivacy')}
            </a>
          </nav>
        </div>
      </footer>

      {/* ---------- Unit detail ---------- */}
      {unit && (
        <div className="sc-sheet-wrap">
          <div className="sc-scrim" onClick={closeOverlay} />
          <div className="sc-sheet" role="dialog" aria-modal="true" aria-label={unit.title || 'Unit'}>
            <div className="sc-sheet-grip" aria-hidden="true" />
            <button className="sc-sheet-close" onClick={closeOverlay} aria-label={t('scClose')}>
              <X size={17} strokeWidth={2} />
            </button>

            <div className="sc-sheet-body">
              {unit.photoUrl && (
                <span className="sc-sheet-photo">
                  <img src={unit.photoUrl} alt={unit.title || 'Unit'} />
                </span>
              )}

              <h3 className="sc-sheet-title">{unit.title || t('scUntitledUnit')}</h3>

              {unit.location && (
                <p className="sc-unit-loc sc-unit-loc--sheet">
                  <MapPin size={12} strokeWidth={2} />{unit.location}
                </p>
              )}

              <div className="sc-price-row">
                <span className="sc-price-big">{formatAedFull(unit.askingPrice) || t('scPriceOnRequest')}</span>
                {unit.isDistress && unit.discountPercent != null && (
                  <span className="sc-unit-flag sc-unit-flag--static">
                    {unit.discountPercent}% {t('scBelowMarket')}
                  </span>
                )}
              </div>

              {unit.isDistress && unit.marketValue != null && (
                <p className="sc-price-note">
                  {t('scMarketValue')}: {formatAedFull(unit.marketValue)}
                </p>
              )}

              <ul className="sc-spec-list">
                {bedLabel(unit.bedrooms) && (
                  <li><BedDouble size={13} strokeWidth={2} /><span>{bedLabel(unit.bedrooms)}</span></li>
                )}
                {unit.sizeSqft != null && (
                  <li><Ruler size={13} strokeWidth={2} /><span>{unit.sizeSqft.toLocaleString('en-AE')} {t('scSqft')}</span></li>
                )}
                {unit.propertyType && (
                  <li><Check size={13} strokeWidth={2.4} /><span>{unit.propertyType}</span></li>
                )}
              </ul>

              {unit.description && <p className="sc-sheet-desc sc-sheet-desc--pre">{unit.description}</p>}

              <div className="sc-lead">
                {leadState === 'done' ? (
                  <div className="sc-lead-ok">
                    <CheckCircle2 size={18} strokeWidth={2} />
                    <p>{t('scLeadDone')}</p>
                  </div>
                ) : (
                  <>
                    <p className="sc-lead-title">{t('scLeadTitle')}</p>
                    <p className="sc-lead-sub">{t('scLeadSub')}</p>

                    <div className="sc-lead-form">
                      <input
                        type="text"
                        autoComplete="name"
                        placeholder={t('scLeadName')}
                        value={lead.name}
                        onChange={(e) => setLead({ ...lead, name: e.target.value })}
                      />
                      <input
                        type="email"
                        autoComplete="email"
                        placeholder={t('scLeadEmail')}
                        value={lead.email}
                        onChange={(e) => setLead({ ...lead, email: e.target.value })}
                      />
                      <input
                        type="tel"
                        autoComplete="tel"
                        placeholder={t('scLeadPhone')}
                        value={lead.phone}
                        onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                      />
                      <textarea
                        placeholder={t('scLeadMsg')}
                        value={lead.message}
                        onChange={(e) => setLead({ ...lead, message: e.target.value })}
                      />
                      <button
                        className="sc-cta sc-cta--primary"
                        onClick={sendLead}
                        disabled={leadState === 'sending'}
                      >
                        <Send size={14} strokeWidth={2} />
                        {leadState === 'sending' ? t('scLeadSending') : t('scLeadSend')}
                      </button>
                    </div>

                    {leadHint && <p className="sc-lead-err">{leadHint}</p>}
                    {leadState === 'error' && <p className="sc-lead-err">{t('scLeadErr')}</p>}
                    <p className="sc-lead-legal">{t('scLeadLegal')}</p>
                  </>
                )}
              </div>
            </div>

            <div className="sc-sheet-foot">
              <button className="sc-cta sc-cta--primary" onClick={() => enquireUnit(unit)}>
                <MessageCircle size={15} strokeWidth={2} />
                {t('scAskOnWhatsApp')}
              </button>
              <button className="sc-cta" onClick={() => { void openCalendly(); }}>
                <CalendarDays size={15} strokeWidth={2} />
                {t('scBookCall')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Service detail ---------- */}
      {active && (
        <div className="sc-sheet-wrap">
          <div className="sc-scrim" onClick={closeOverlay} />
          <div className="sc-sheet" role="dialog" aria-modal="true" aria-label={de ? active.titleDe : active.title}>
            <div className="sc-sheet-grip" aria-hidden="true" />
            <button className="sc-sheet-close" onClick={closeOverlay} aria-label={t('scClose')}>
              <X size={17} strokeWidth={2} />
            </button>

            <div className="sc-sheet-body">
              <span className="sc-tile sc-tile--lg"><TileIcon icon={active.icon} /></span>
              <h3 className="sc-sheet-title">
                {de ? active.titleDe : active.title}
                {badges(active)}
              </h3>
              <p className="sc-sheet-desc">{de ? active.descriptionDe : active.description}</p>

              {(de ? active.documentsDe : active.documents).length > 0 && (
                <>
                  <p className="sc-sheet-label">{t('scWhatWeNeed')}</p>
                  <ul className="sc-docs">
                    {(de ? active.documentsDe : active.documents).map((doc) => (
                      <li key={doc}>
                        <Check size={12} strokeWidth={2.6} />
                        <span>{doc}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {(de ? active.noteDe : active.note) && (
                <p className="sc-sheet-note">{de ? active.noteDe : active.note}</p>
              )}
            </div>

            <div className="sc-sheet-foot">
              <button className="sc-cta sc-cta--primary" onClick={() => enquireService(active)}>
                <MessageCircle size={15} strokeWidth={2} />
                {t('scAskOnWhatsApp')}
              </button>
              <button className="sc-cta" onClick={() => { void openCalendly(); }}>
                <CalendarDays size={15} strokeWidth={2} />
                {t('scBookCall')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- In-app booking frame: journey ticket and stays ---------- */}
      {frame && (
        <div className="sc-frame-wrap" role="dialog" aria-modal="true" aria-label={frame.label}>
          <div className="sc-frame-bar">
            <button className="sc-back" onClick={closeOverlay}>
              <ArrowLeft size={15} strokeWidth={2} />
              <span>{t('scBack')}</span>
            </button>
            {/* Escape hatch: Ablefy may refuse framing on a later checkout step. */}
            <button
              className="sc-frame-out"
              onClick={() => window.open(frame.url, '_blank', 'noopener,noreferrer')}
            >
              {t('scOpenInBrowser')}
              <ExternalLink size={13} strokeWidth={2} />
            </button>
          </div>

          <iframe
            className="sc-frame"
            src={frame.url}
            title={frame.label}
            allow="payment"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      )}
    </div>
  );
};

export default ServicesShowcase;
